// components/RendersPanel.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gsap } from 'gsap';
import { 
  FiArrowLeft, FiHeart, FiDownload, FiPlus, 
  FiCopy, FiEdit2, FiImage, FiX, FiCamera, FiTrash2, FiGrid, FiUpload, FiVideo, FiCheck
} from 'react-icons/fi';
import { FaRegTrashCan } from 'react-icons/fa6';
import { IoIosArrowUp, IoIosArrowDown, IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { IoWarningOutline } from 'react-icons/io5';
import { RiFilmAiLine, RiCameraLine, RiGuideLine } from 'react-icons/ri';
import { LuRewind, LuShrink } from 'react-icons/lu';
import { MdKeyboardArrowRight, MdFlashOn, MdFlashOff, MdFlashAuto } from 'react-icons/md';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { doc, updateDoc, deleteDoc, collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase';
import { SkeletonShimmer } from '../components/SkeletonComponents.jsx';
import GuidedCameraOverlay from './GuidedCameraOverlay';
import { 
  calculateVideoGenerationCost, 
  checkSufficientCredits, 
  deductVideoCredits,
  getRemainingCredits,
  safeGenerate
} from '../services/usageService';

// ============================================
// THUMBNAIL GENERATION HELPER
// ============================================
const generateVideoThumbnail = (videoUrl, renderId, authUser, setUserRenders) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    
    video.onloadeddata = () => {
      video.currentTime = 1;
    };
    
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
      
      const saveThumbnail = async () => {
        if (!authUser) return;
        try {
          const renderRef = doc(db, 'renders', renderId);
          await updateDoc(renderRef, { thumbnailUrl });
          setUserRenders(prev => prev.map(r => 
            r.id === renderId ? { ...r, thumbnailUrl } : r
          ));
        } catch (error) {
          console.error('Error saving thumbnail:', error);
        }
      };
      saveThumbnail();
      resolve(thumbnailUrl);
    };
    
    video.onerror = reject;
    video.src = videoUrl;
  });
};

// Extract a thumbnail frame from a video blob (for motion references)
const extractThumbnailFromBlob = (blob) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const url = URL.createObjectURL(blob);
    video.src = url;
    
    let resolved = false;
    
    const captureFrame = () => {
      if (resolved) return;
      resolved = true;
      try {
        const canvas = document.createElement('canvas');
        const size = Math.min(video.videoWidth || 720, video.videoHeight || 720);
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const sx = ((video.videoWidth || 720) - size) / 2;
        const sy = ((video.videoHeight || 720) - size) / 2;
        ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    
    video.onloadeddata = () => {
      if (video.duration) {
        video.currentTime = Math.min(0.5, video.duration / 2);
      } else {
        captureFrame();
      }
    };
    
    video.onseeked = captureFrame;
    
    // Fallback: if no seek happens within 1s, capture anyway
    setTimeout(() => {
      if (!resolved) {
        captureFrame();
      }
    }, 1000);
    
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load video for thumbnail'));
    };
  });
};

// ============================================
// FRESH URL HELPER
// ============================================
const getFreshVideoUrl = async (render) => {
  if (!render) return null;
  
  if (render.videoStoragePath) {
    try {
      const storage = getStorage();
      const storageRef = ref(storage, render.videoStoragePath);
      const freshUrl = await getDownloadURL(storageRef);
      return freshUrl;
    } catch (error) {
      console.warn('Could not refresh video URL from storage path:', error.message);
    }
  }
  
  if (render.videoUrl && render.videoUrl.includes('firebasestorage.googleapis.com')) {
    try {
      const urlParts = render.videoUrl.split('/o/');
      if (urlParts.length > 1) {
        const encodedPath = urlParts[1].split('?')[0];
        const decodedPath = decodeURIComponent(encodedPath);
        const storage = getStorage();
        const storageRef = ref(storage, decodedPath);
        const freshUrl = await getDownloadURL(storageRef);
        return freshUrl;
      }
    } catch (error) {
      console.warn('Could not extract and refresh from videoUrl:', error.message);
    }
  }
  
  if (render.originalProviderUrl) {
    return render.originalProviderUrl;
  }
  
  return render.videoUrl;
};

// ============================================
// REVERSE VIDEO HELPER
// ============================================
const reverseVideo = async (render, authUser, setGenerationStatus, setIsGeneratingThumbnail) => {
  if (!render || !authUser) return null;
  
  return new Promise(async (resolve, reject) => {
    try {
      const videoUrl = await getFreshVideoUrl(render);
      if (!videoUrl) {
        reject(new Error('Could not get video URL'));
        return;
      }
      
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const videoBlobUrl = URL.createObjectURL(blob);
      
      const video = document.createElement('video');
      video.src = videoBlobUrl;
      video.muted = true;
      video.crossOrigin = 'anonymous';
      
      video.onloadedmetadata = async () => {
        const duration = video.duration;
        const width = video.videoWidth;
        const height = video.videoHeight;
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        const fps = 24;
        const totalFrames = Math.ceil(duration * fps);
        const frames = [];
        
        setGenerationStatus(`Analyzing video (0/${totalFrames} frames)...`);
        setIsGeneratingThumbnail(true);
        
        for (let i = 0; i < totalFrames; i++) {
          const time = i / fps;
          if (time > duration) break;
          
          await new Promise((resolveFrame) => {
            video.currentTime = time;
            video.onseeked = () => {
              try {
                ctx.drawImage(video, 0, 0, width, height);
                const frameData = canvas.toDataURL('image/jpeg', 0.7);
                frames.push(frameData);
                setGenerationStatus(`Capturing frames (${i + 1}/${totalFrames})...`);
                resolveFrame();
              } catch (err) {
                console.warn('Frame capture error:', err);
                resolveFrame();
              }
            };
          });
        }
        
        setGenerationStatus(`Reversing ${frames.length} frames...`);
        
        const reverseCanvas = document.createElement('canvas');
        reverseCanvas.width = width;
        reverseCanvas.height = height;
        const reverseCtx = reverseCanvas.getContext('2d');
        
        const stream = reverseCanvas.captureStream(fps);
        const mediaRecorder = new MediaRecorder(stream, { 
          mimeType: 'video/webm',
          videoBitsPerSecond: 4000000
        });
        
        const chunks = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        
        mediaRecorder.onstop = async () => {
          const reversedBlob = new Blob(chunks, { type: 'video/webm' });
          const reader = new FileReader();
          
          reader.onloadend = async () => {
            const base64Video = reader.result;
            const storage = getStorage();
            const timestamp = Date.now();
            const storagePath = `users/${authUser.uid}/renders/${timestamp}_reversed.webm`;
            const storageRef = ref(storage, storagePath);
            
            setGenerationStatus('Uploading reversed video...');
            await uploadString(storageRef, base64Video, 'data_url');
            const permanentUrl = await getDownloadURL(storageRef);
            
            URL.revokeObjectURL(videoBlobUrl);
            
            resolve({
              videoUrl: permanentUrl,
              videoStoragePath: storagePath
            });
          };
          
          reader.onerror = () => {
            reject(new Error('Failed to read reversed blob'));
          };
          
          reader.readAsDataURL(reversedBlob);
        };
        
        mediaRecorder.start();
        
        for (let i = frames.length - 1; i >= 0; i--) {
          const img = new Image();
          await new Promise((resolveImg) => {
            img.onload = () => {
              try {
                reverseCtx.drawImage(img, 0, 0, width, height);
                setGenerationStatus(`Rendering reversed video (${frames.length - i}/${frames.length})...`);
                resolveImg();
              } catch (err) {
                console.warn('Frame draw error:', err);
                resolveImg();
              }
            };
            img.onerror = () => {
              console.warn('Image load error for frame', i);
              resolveImg();
            };
            img.src = frames[i];
          });
          await new Promise(resolve => setTimeout(resolve, 1000 / fps));
        }
        
        mediaRecorder.stop();
      };
      
      video.onerror = (err) => {
        console.error('Video load error:', err);
        URL.revokeObjectURL(videoBlobUrl);
        reject(new Error('Failed to load video'));
      };
      
      video.load();
      
    } catch (error) {
      console.error('Reverse video error:', error);
      reject(error);
    }
  });
};

// ============================================
// MOTION REFERENCE HELPERS
// ============================================
const getCameraStream = async () => {
  try {
    const constraints = {
      audio: false,
      video: { 
        facingMode: { exact: "environment" },
        width: { ideal: 3840 },
        height: { ideal: 2160 }
      }
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    const videoTrack = stream.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities?.() || {};
    
    // Default to 1x - don't force 0.5x
    return stream;
  } catch (error) {
    // Fallback without exact facing mode
    const fallbackStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' } }
    });
    return fallbackStream;
  }
};

// ============================================
// MAIN RENDERS PANEL COMPONENT
// ============================================
const RendersPanel = ({ 
  authUser, 
  onClose,
  userRenders: externalUserRenders,
  setUserRenders: externalSetUserRenders,
  imagesRemaining,
  videosRemaining,
  updateRemainingCounts,
  isPWA,
  initialImage
}) => {
  // Local state for renders (can use external or internal)
  const [userRenders, setUserRenders] = useState(externalUserRenders || []);
  const [rendersLoading, setRendersLoading] = useState(false);
  const [favoriteFilter, setFavoriteFilter] = useState(false);
  const [selectedRender, setSelectedRender] = useState(null);
  const [showRenderDetails, setShowRenderDetails] = useState(false);
  const [headerText, setHeaderText] = useState('Renders');
  const rendersScrollContainerRef = useRef(null);
  
  const [showGuidedCamera, setShowGuidedCamera] = useState(() => {
  // Only show guided camera on mobile by default
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 767;
  return isMobileDevice;
});
// Add this new state for tracking if initial image was loaded
const [hasLoadedInitialImage, setHasLoadedInitialImage] = useState(false);

  const [guidedData, setGuidedData] = useState(null);
  const [guidedCameraKey, setGuidedCameraKey] = useState(0); // Add this for remounting
  const [guidedModeOpenedManually, setGuidedModeOpenedManually] = useState(false);
  
  // User data for credit checking
  const [userData, setUserData] = useState(null);

  // ============================================
  // PRODUCTION & LOCAL API URL HELPER
  // ============================================
  const getReplicateBaseUrl = () => {
    if (import.meta.env.DEV) return '/api/replicate';
    return 'https://outlne.com/.netlify/functions/replicate-proxy';
  };
  
  const getFalBaseUrl = () => {
    if (import.meta.env.DEV) return '/fal-proxy';
    return 'https://outlne.com/.netlify/functions/fal-proxy';
  };
  
  // Generation states
  const [renderUploadedImage, setRenderUploadedImage] = useState(null);
  const [renderUploadPreview, setRenderUploadPreview] = useState(null);
  const [renderPrompt, setRenderPrompt] = useState('');
  const [renderDuration, setRenderDuration] = useState(7);
  const [renderResolution, setRenderResolution] = useState('720p');
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationCost, setGenerationCost] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Guided mode preview state (for mobile camera flow)
  const [guidedImagePreview, setGuidedImagePreview] = useState(null);
  const [guidedImageUploaded, setGuidedImageUploaded] = useState(null);
  
  
  // Handle initialImage prop from Editor canvas - sets image in input form
useEffect(() => {
  if (initialImage && !renderUploadPreview) {
    console.log('RendersPanel: Setting image from canvas', { hasImage: !!initialImage });
    setRenderUploadPreview(initialImage);
    setRenderUploadedImage(null);
    setGuidedImagePreview(initialImage);
    setGuidedImageUploaded(null);
    setHasLoadedInitialImage(true);
  }
}, [initialImage, renderUploadPreview]);



// Ensure guidedImagePreview stays in sync with initialImage when guided mode reopens
useEffect(() => {
  if (initialImage && !guidedImagePreview && !renderUploadPreview) {
    setGuidedImagePreview(initialImage);
  }
}, [initialImage, guidedImagePreview, renderUploadPreview]);
 
  
  // Edit menu states
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [showRenameRenderPopup, setShowRenameRenderPopup] = useState(false);
  const [renderToRename, setRenderToRename] = useState(null);
  const [newRenderName, setNewRenderName] = useState('');
  const [showDeleteRenderPopup, setShowDeleteRenderPopup] = useState(false);
  const [renderToDelete, setRenderToDelete] = useState(null);
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const [thumbnailPickerRender, setThumbnailPickerRender] = useState(null);
  const [thumbnailSeekTime, setThumbnailSeekTime] = useState(1);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  
    // Motion reference states
  const [motionVideoBlob, setMotionVideoBlob] = useState(null);
  const [motionVideoPreview, setMotionVideoPreview] = useState(null);
  const [motionVideoStoragePath, setMotionVideoStoragePath] = useState(null);
  const [motionVideoUrl, setMotionVideoUrl] = useState(null);
  const [motionVideoDuration, setMotionVideoDuration] = useState(0);
  const [motionThumbnailUrl, setMotionThumbnailUrl] = useState(null);
  const [showMotionRecorder, setShowMotionRecorder] = useState(false);
  const [isRecordingMotion, setIsRecordingMotion] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [cameraStream, setCameraStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showMotionPreview, setShowMotionPreview] = useState(false);
  const [motionPreviewBlobUrl, setMotionPreviewBlobUrl] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [cameraZoomLevel, setCameraZoomLevel] = useState(0.5);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  
  // Refs
  const renderFileInputRef = useRef(null);
  const motionFileInputRef = useRef(null);
  const motionVideoRef = useRef(null);
  const motionPreviewVideoRef = useRef(null);
  const guidedFileInputRef = useRef(null);
  const isSwitchingToMyRendersRef = useRef(false);
  
  // Scroll offset constants
  const VIEW_RENDERS_SCROLL_OFFSET = -60;
  const GENERATE_AGAIN_SCROLL_OFFSET = -70;

  // Ensure panel doesn't auto-open on mount
  useEffect(() => {
    return () => {
      // Cleanup all states when component unmounts
      setShowRenderDetails(false);
      setSelectedRender(null);
      setEditMenuOpen(false);
      setRenderUploadPreview(null);
      setRenderUploadedImage(null);
      setRenderPrompt('');
      setMotionVideoUrl(null);
      setMotionVideoPreview(null);
      setMotionVideoStoragePath(null);
      setMotionVideoBlob(null);
      setMotionVideoDuration(0);
      setMotionThumbnailUrl(null);
      setShowMotionRecorder(false);
      setIsRecordingMotion(false);
      setShowMotionPreview(false);
      
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      if (motionVideoRef.current) {
        motionVideoRef.current.srcObject = null;
      }
    };
  }, []);
  
  // ============================================
  // FETCH RENDERS
  // ============================================
  const fetchUserRenders = useCallback(async () => {
    if (!authUser) return;
    
    setRendersLoading(true);
    try {
      const rendersRef = collection(db, 'renders');
      const q = query(
        rendersRef,
        where('userId', '==', authUser.uid),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const renders = [];
      querySnapshot.forEach((docSnap) => {
        renders.push({ id: docSnap.id, ...docSnap.data() });
      });
      setUserRenders(renders);
      if (externalSetUserRenders) externalSetUserRenders(renders);
    } catch (error) {
      console.error('Error fetching renders:', error);
    } finally {
      setRendersLoading(false);
    }
  }, [authUser, externalSetUserRenders]);
  
  // ============================================
  // CALCULATE COST
  // ============================================
  const calculateRenderCost = useCallback((duration, hasMotionReference = false) => {
    let baseCost = duration * 4;
    if (hasMotionReference) baseCost = baseCost * 2.5;
    return Math.round(baseCost);
  }, []);
  
  // ============================================
  // MOTION REFERENCE HELPERS (for after guided flow)
  // ============================================
  const startMotionRecording = () => {
  if (isRecordingMotion) return;
  
  // Clean up previous preview URLs
  if (motionPreviewBlobUrl) {
    URL.revokeObjectURL(motionPreviewBlobUrl);
    setMotionPreviewBlobUrl(null);
  }
  
  setMotionVideoBlob(null);
  setMotionVideoPreview(null);
  setMotionVideoUrl(null);
  setMotionVideoStoragePath(null);
  setMotionVideoDuration(0);
  setShowMotionPreview(false);
  setRecordingDuration(0);
  
  const chunks = [];
  const startTime = Date.now();
  let isRecordingActive = true;
  let localMediaRecorder = null;
  
  // Timer for UI display - use startTime for consistent timing
  const updateTimer = () => {
    if (!isRecordingActive) return;
    const elapsed = (Date.now() - startTime) / 1000;
    setRecordingDuration(parseFloat(elapsed.toFixed(1)));
    if (elapsed >= 15) {
      isRecordingActive = false;
      if (localMediaRecorder && localMediaRecorder.state === 'recording') {
        localMediaRecorder.stop();
      }
      return;
    }
    if (isRecordingActive) {
      requestAnimationFrame(updateTimer);
    }
  };
  
  setIsRecordingMotion(true);
  
  // Ensure we have a live stream
  const getStream = async () => {
    // Always get a fresh stream for recording
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    // Use pre-initialized stream if available for faster start
    let newStream;
    if (preInitializedStream && preInitializedStream.active && preInitializedStream.getVideoTracks()[0]?.readyState === 'live') {
      newStream = preInitializedStream;
      console.log('Using pre-initialized stream for recording');
      // Don't re-set preInitializedStream, just use it
    } else {
      newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: 'environment', width: 1280, height: 720 }
      });
      preInitializedStream = newStream;
    }
    
    setCameraStream(newStream);
    setIsCameraReady(true);
    return newStream;
  };
  
  getStream().then(stream => {
    // Update video element with the fresh stream
    if (motionVideoRef.current) {
      motionVideoRef.current.srcObject = stream;
    }
    
    // Wait for the stream to be fully ready
    const waitForStream = () => {
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0 && videoTracks[0].readyState === 'live') {
        startRecordingWithStream(stream);
      } else {
        // Check again after a short delay
        setTimeout(() => {
          const tracks = stream.getVideoTracks();
          if (tracks.length > 0 && tracks[0].readyState === 'live') {
            startRecordingWithStream(stream);
          } else if (tracks.length > 0) {
            // Track exists but not live yet, wait more
            console.log('Waiting for track to become live, current state:', tracks[0].readyState);
            waitForStream();
          } else {
            console.error('No video tracks available');
            isRecordingActive = false;
            setIsRecordingMotion(false);
            alert('Camera stream is not ready. Please try again.');
          }
        }, 200);
      }
    };
    
    const startRecordingWithStream = (stream) => {
      // Determine supported MIME type
      let mimeType = '';
      const candidates = [
        'video/webm;codecs=vp8',
        'video/webm;codecs=vp9',
        'video/webm',
        'video/mp4'
      ];
      for (const type of candidates) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      let recorder;
      try {
        if (mimeType) {
          recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2500000 });
        } else {
          recorder = new MediaRecorder(stream);
        }
      } catch (e) {
        console.error('Failed to create MediaRecorder:', e);
        try {
          recorder = new MediaRecorder(stream);
        } catch (e2) {
          console.error('Failed to create MediaRecorder fallback:', e2);
          isRecordingActive = false;
          setIsRecordingMotion(false);
          alert('Recording not supported on this device. Please try uploading a video instead.');
          return;
        }
      }
      
      localMediaRecorder = recorder;
      setMediaRecorder(recorder);
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        isRecordingActive = false;
        setIsRecordingMotion(false);
      };
      
      recorder.onstop = () => {
    isRecordingActive = false;
    setIsRecordingMotion(false);
    
    if (chunks.length === 0) {
      console.warn('No data recorded');
      return;
    }
    
    // Determine the best MIME type for the blob
    const mimeType = recorder.mimeType || 'video/webm';
    const blob = new Blob(chunks, { type: mimeType });
    setMotionVideoBlob(blob);
    
    // Create ONE blob URL that will be used for both duration check and preview
    const previewUrl = URL.createObjectURL(blob);
    console.log('Recording complete. Blob type:', blob.type, 'Size:', blob.size);
    
    // Store the URL first
    setMotionPreviewBlobUrl(previewUrl);
    setMotionVideoPreview(previewUrl);
    
    // Get accurate duration using a SEPARATE blob URL so we don't revoke the preview one
    const durationCheckUrl = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.playsInline = true;
    video.muted = true;
    
    // Set a fallback duration immediately so preview shows with timer value
    const timerDuration = parseFloat(((Date.now() - startTime) / 1000).toFixed(1));
    setMotionVideoDuration(timerDuration);
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      console.log('Recorded video duration:', duration);
      if (duration && duration > 0 && duration !== Infinity) {
        setMotionVideoDuration(parseFloat(duration.toFixed(1)));
      }
      // Revoke the duration-check URL only, not the preview URL
      URL.revokeObjectURL(durationCheckUrl);
    };
    video.onerror = () => {
      console.log('Duration check failed, using timer duration');
      URL.revokeObjectURL(durationCheckUrl);
    };
    video.src = durationCheckUrl;
    video.load();
    
    // Show preview IMMEDIATELY (don't wait for metadata)
    setShowMotionPreview(true);
  };
      
      try {
        recorder.start(1000);
        // Start the UI timer using requestAnimationFrame for smooth updates
        requestAnimationFrame(updateTimer);
        console.log('MediaRecorder started with mimeType:', recorder.mimeType);
      } catch (e) {
        console.error('Failed to start recording:', e);
        isRecordingActive = false;
        setIsRecordingMotion(false);
        alert('Failed to start recording. Please try again.');
      }
    };
    
    // Start the stream readiness check
    waitForStream();
    
  }).catch(err => {
    console.error('Failed to get camera stream:', err);
    setIsRecordingMotion(false);
    alert('Unable to access camera. Please check permissions.');
  });
};
  
  const uploadMotionVideoToStorage = async (blob) => {
    if (!authUser) return null;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Video = reader.result;
          const storage = getStorage();
          const path = `users/${authUser.uid}/motion-references/${Date.now()}.mp4`;
          const storageRef = ref(storage, path);
          
          setGenerationStatus('Uploading motion reference...');
          await uploadString(storageRef, base64Video, 'data_url');
          const permanentUrl = await getDownloadURL(storageRef);
          
          setMotionVideoStoragePath(path);
          setMotionVideoUrl(permanentUrl);
          resolve(permanentUrl);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  const handleConfirmMotion = async () => {
    if (!motionVideoBlob) return;
    
    setGenerationStatus('Processing motion reference...');
    try {
      const permanentUrl = await uploadMotionVideoToStorage(motionVideoBlob);
      if (permanentUrl) {
        if (motionPreviewBlobUrl) {
          URL.revokeObjectURL(motionPreviewBlobUrl);
          setMotionPreviewBlobUrl(null);
        }
        if (motionVideoPreview && motionVideoPreview !== motionPreviewBlobUrl) {
          URL.revokeObjectURL(motionVideoPreview);
        }
        setShowMotionPreview(false);
        setShowMotionRecorder(false);
        setGenerationStatus('');
        setCameraZoomLevel(0.5);
        // Generate thumbnail for the motion reference
        if (motionVideoBlob) {
          extractThumbnailFromBlob(motionVideoBlob)
            .then(thumb => setMotionThumbnailUrl(thumb))
            .catch(err => console.warn('Thumbnail extraction failed:', err));
        }
        if (cameraStream) {
          cameraStream.getTracks().forEach(track => track.stop());
          setCameraStream(null);
        }
      }
    } catch (error) {
      console.error('Failed to upload motion video:', error);
      alert('Failed to save motion reference. Please try again.');
    }
  };
  
 const handleReRecordMotion = async () => {
  if (motionPreviewBlobUrl) {
    URL.revokeObjectURL(motionPreviewBlobUrl);
    setMotionPreviewBlobUrl(null);
  }
  setMotionVideoBlob(null);
  setMotionVideoPreview(null);
  setMotionVideoDuration(0);
  setShowMotionPreview(false);
  setRecordingDuration(0);
  setIsRecordingMotion(false);
  setCameraZoomLevel(0.5);
  
  if (recordingTimer) {
    clearInterval(recordingTimer);
    setRecordingTimer(null);
  }
  
  // Reset MediaRecorder
  setMediaRecorder(null);
  
  // Always get a fresh stream for re-record (don't reuse old one)
  if (cameraStream) {
    cameraStream.getTracks().forEach(track => track.stop());
    setCameraStream(null);
  }
  
  try {
    // Use pre-initialized stream if available
    let newStream;
    if (preInitializedStream && preInitializedStream.active && preInitializedStream.getVideoTracks()[0]?.readyState === 'live') {
      newStream = preInitializedStream;
      console.log('Using pre-initialized stream for re-record');
    } else {
      newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      preInitializedStream = newStream;
    }
    
    setCameraStream(newStream);
    
    // Apply 0.5x zoom to the fresh stream
    try {
      const videoTrack = newStream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities?.() || {};
      if (capabilities.zoom) {
        const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
        await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
      }
    } catch (zoomErr) {
      console.warn('Could not apply zoom on re-record:', zoomErr);
    }
    
    if (motionVideoRef.current) {
      if (motionVideoRef.current.srcObject) {
        motionVideoRef.current.srcObject = null;
      }
      motionVideoRef.current.srcObject = newStream;
      // Don't await play - let it play naturally
      motionVideoRef.current.play().catch(e => console.warn('play error on re-record:', e.message));
    }
    
    // Camera is already shown, just mark as ready silently
    setIsCameraReady(true);
    setShowMotionPreview(false);
    
  } catch (err) {
    console.error('Failed to get new stream for re-record:', err);
    setCameraPermissionDenied(true);
    return;
  }
};
  
  const handleRemoveMotionReference = () => {
    setMotionVideoUrl(null);
    setMotionVideoPreview(null);
    setMotionVideoStoragePath(null);
    setMotionVideoBlob(null);
    setMotionVideoDuration(0);
    setMotionThumbnailUrl(null);
  };
  
  const stopMotionRecording = () => {
  // We no longer use recordingTimer with requestAnimationFrame,
  // but keep this for compatibility
  if (recordingTimer) {
    clearInterval(recordingTimer);
    setRecordingTimer(null);
  }
  
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    try {
      // Request final data chunk before stopping
      mediaRecorder.requestData();
      // Small delay to ensure data is collected
      setTimeout(() => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          console.log('MediaRecorder stop requested');
        }
      }, 150);
    } catch (e) {
      console.error('MediaRecorder stop failed:', e);
      setIsRecordingMotion(false);
    }
  } else {
    setIsRecordingMotion(false);
  }
};
  
  const cancelMotionRecording = () => {
    if (recordingTimer) {
      cancelAnimationFrame(recordingTimer);
      setRecordingTimer(null);
    }
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (motionPreviewBlobUrl) {
      URL.revokeObjectURL(motionPreviewBlobUrl);
      setMotionPreviewBlobUrl(null);
    }
    setIsRecordingMotion(false);
    setRecordingDuration(0);
    setShowMotionRecorder(false);
    setIsCameraReady(false);
    setCameraPermissionDenied(false);
    setMotionVideoBlob(null);
    setMotionVideoPreview(null);
    setCameraZoomLevel(0.5);
  };
  
  // Pre-initialize camera stream in background
let preInitializedStream = null;

// Function to pre-initialize camera silently
const preInitializeCamera = async () => {
  if (preInitializedStream) return;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { 
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    });
    preInitializedStream = stream;
    // Don't display it yet, just keep it ready
    console.log('Camera pre-initialized for quick recording');
  } catch (err) {
    console.warn('Camera pre-initialization failed:', err);
  }
};

// Call pre-initialization when component mounts or when user might use it
useEffect(() => {
  // Only pre-initialize on desktop/tablet – mobile triggers permissions too early
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 767;
  if (!isMobileDevice) {
    preInitializeCamera();
  }
  
  return () => {
    if (preInitializedStream) {
      preInitializedStream.getTracks().forEach(track => track.stop());
      preInitializedStream = null;
    }
  };
}, []);

const openMotionRecorder = async () => {
  setShowMotionRecorder(true);
  setCameraPermissionDenied(false);
  setIsCameraReady(false);
  setCameraZoomLevel(0.5);
  setRecordingDuration(0);
  setIsRecordingMotion(false);
  setMotionVideoBlob(null);
  setMotionVideoPreview(null);
  setMotionPreviewBlobUrl(null);
  setMotionVideoDuration(0);
  
  try {
    // Use pre-initialized stream if available
    let stream;
    if (preInitializedStream && preInitializedStream.active && preInitializedStream.getVideoTracks()[0]?.readyState === 'live') {
      stream = preInitializedStream;
      console.log('Using pre-initialized camera stream');
    } else {
      // Fallback: get fresh stream
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      preInitializedStream = stream;
    }
    
    // Store stream first
    setCameraStream(stream);
    
    if (motionVideoRef.current) {
      // Clear any previous srcObject first
      if (motionVideoRef.current.srcObject) {
        motionVideoRef.current.srcObject = null;
      }
      
      // Small delay before setting new srcObject
      await new Promise(resolve => setTimeout(resolve, 50));
      
      motionVideoRef.current.srcObject = stream;
      motionVideoRef.current.setAttribute('playsinline', 'true');
      motionVideoRef.current.muted = true;
      motionVideoRef.current.loop = false;
      
      // Wait for video metadata to load with timeout
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.warn('Video metadata load timeout, continuing anyway');
            resolve(); // Don't reject, continue with partial readiness
          }, 5000);
          
          const onReady = () => {
            clearTimeout(timeout);
            motionVideoRef.current?.removeEventListener('loadedmetadata', onReady);
            motionVideoRef.current?.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = () => {
            clearTimeout(timeout);
            motionVideoRef.current?.removeEventListener('loadedmetadata', onReady);
            motionVideoRef.current?.removeEventListener('error', onError);
            console.warn('Video element error, continuing anyway');
            resolve(); // Don't reject, continue with partial readiness
          };
          
          if (motionVideoRef.current?.readyState >= 1) {
            clearTimeout(timeout);
            resolve();
          } else {
            motionVideoRef.current?.addEventListener('loadedmetadata', onReady, { once: true });
            motionVideoRef.current?.addEventListener('error', onError, { once: true });
          }
        });
      } catch (e) {
        console.warn('Error waiting for video metadata:', e);
      }
      
      // Try to play but don't block on it
      try {
        await motionVideoRef.current.play();
        console.log('Motion recorder camera playback started');
      } catch (playError) {
        console.warn('Autoplay prevented, but stream is valid:', playError.message);
      }
    }
    
    // Apply default 0.5x zoom to the camera
    try {
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities?.() || {};
      if (capabilities.zoom) {
        const targetZoom = Math.min(Math.max(0.5, capabilities.zoom.min || 0.5), capabilities.zoom.max || 1);
        await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
      }
    } catch (zoomErr) {
      console.warn('Could not apply default zoom:', zoomErr);
    }
    
    // Always mark as ready if we have the stream
    setIsCameraReady(true);
    
  } catch (error) {
    console.error('Camera access error:', error);
    setCameraPermissionDenied(true);
    setIsCameraReady(false);
  }
};
  
  const handleMotionFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file (MP4, MOV, etc.)');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      alert('Video must be less than 50MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Video = reader.result;
      setMotionVideoBlob(file);
      const previewUrl = URL.createObjectURL(file);
      setMotionVideoPreview(previewUrl);
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        setMotionVideoDuration(video.duration);
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(file);
      
      setGenerationStatus('Uploading motion reference...');
      try {
        const storage = getStorage();
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop() || 'mp4';
        const path = `users/${authUser.uid}/motion-references/${timestamp}.${fileExtension}`;
        const storageRef = ref(storage, path);
        await uploadString(storageRef, base64Video, 'data_url');
        const permanentUrl = await getDownloadURL(storageRef);
        setMotionVideoStoragePath(path);
        setMotionVideoUrl(permanentUrl);
        setGenerationStatus('');
        setCameraZoomLevel(0.5);
        // Generate thumbnail from the uploaded file
        extractThumbnailFromBlob(file)
          .then(thumb => setMotionThumbnailUrl(thumb))
          .catch(err => console.warn('Thumbnail extraction failed:', err));
      } catch (error) {
        console.error('Failed to upload motion video:', error);
        alert('Failed to save motion reference. Please try again.');
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  
  // ============================================
  // UPDATE COST WHEN MOTION REFERENCE CHANGES
  // ============================================
  useEffect(() => {
    const hasMotionRef = !!motionVideoUrl;
    setGenerationCost(calculateRenderCost(renderDuration, hasMotionRef));
  }, [renderDuration, motionVideoUrl, calculateRenderCost]);
  
  // Fetch user data for credit checking
  useEffect(() => {
    if (!authUser) return;
    const fetchUserData = async () => {
      try {
        const userRef = doc(db, 'users', authUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [authUser]);

  // Fetch renders on mount
  useEffect(() => {
    if (authUser) {
      fetchUserRenders();
    }
  }, [authUser, fetchUserRenders]);

    // Guided data is now handled by the GuidedCameraOverlay component
  
  // Sync external renders if provided
  useEffect(() => {
    if (externalUserRenders && JSON.stringify(externalUserRenders) !== JSON.stringify(userRenders)) {
      setUserRenders(externalUserRenders);
    }
  }, [externalUserRenders]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      if (recordingTimer) clearInterval(recordingTimer);
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      // Clean up pre-initialized stream
      if (preInitializedStream) {
        preInitializedStream.getTracks().forEach(track => track.stop());
        preInitializedStream = null;
      }
    };
  }, [cameraStream, recordingTimer, mediaRecorder]);

    // Set motion video stream via ref to avoid React warning
  useEffect(() => {
    if (motionVideoRef.current && cameraStream) {
      motionVideoRef.current.srcObject = cameraStream;
      motionVideoRef.current.play().catch(e => console.warn('autoplay prevented', e));
    }
  }, [cameraStream]);
  
  // ============================================
  // HANDLE IMAGE UPLOAD
  // ============================================
  const handleRenderImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setRenderUploadPreview(reader.result);
      setRenderUploadedImage(file);
    };
    reader.readAsDataURL(file);
  };
  
  // ============================================
  // TOGGLE FAVORITE
  // ============================================
  const toggleFavorite = async (render) => {
    if (!authUser) return;
    
    try {
      const renderRef = doc(db, 'renders', render.id);
      const newFavoriteStatus = !render.isFavorite;
      await updateDoc(renderRef, {
        isFavorite: newFavoriteStatus,
        updatedAt: serverTimestamp(),
      });
      
      setUserRenders(prev => prev.map(r => 
        r.id === render.id ? { ...r, isFavorite: newFavoriteStatus } : r
      ));
      
      if (selectedRender && selectedRender.id === render.id) {
        setSelectedRender({ ...selectedRender, isFavorite: newFavoriteStatus });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };
  
  // ============================================
  // COPY PROMPT
  // ============================================
  const copyPromptToClipboard = (prompt) => {
    navigator.clipboard.writeText(prompt);
    alert('Prompt copied to clipboard!');
  };
  
  // ============================================
  // GENERATE AGAIN
  // ============================================
  const handleGenerateAgain = (render) => {
    setRenderPrompt(render.prompt || '');
    if (render.inputImageUrl) {
      setRenderUploadPreview(render.inputImageUrl);
      setRenderUploadedImage(null);
    }
    setTimeout(() => {
      const generateSection = document.getElementById('generate-section');
      const scrollContainer = rendersScrollContainerRef.current;
      if (generateSection && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = generateSection.getBoundingClientRect();
        const scrollOffset = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        
        scrollContainer.scrollTo({
          top: scrollOffset + GENERATE_AGAIN_SCROLL_OFFSET,
          behavior: 'smooth'
        });
      }
    }, 150);
  };
  
  // ============================================
  // DELETE RENDER
  // ============================================
  const handleDeleteRender = async () => {
    if (!renderToDelete || !authUser) return;
    
    try {
      const renderRef = doc(db, 'renders', renderToDelete.id);
      await deleteDoc(renderRef);
      
      setUserRenders(prev => prev.filter(r => r.id !== renderToDelete.id));
      
      setShowDeleteRenderPopup(false);
      setRenderToDelete(null);
      if (showRenderDetails && selectedRender?.id === renderToDelete.id) {
        setShowRenderDetails(false);
        setSelectedRender(null);
      }
    } catch (error) {
      console.error('Error deleting render:', error);
      alert('Failed to delete render. Please try again.');
    }
  };
  
  // ============================================
  // RENAME RENDER
  // ============================================
  const handleRenameRender = useCallback(async () => {
    if (!renderToRename || !authUser || !newRenderName.trim()) return;
    
    try {
      const renderRef = doc(db, 'renders', renderToRename.id);
      await updateDoc(renderRef, {
        customName: newRenderName.trim(),
        updatedAt: serverTimestamp(),
      });
      
      setUserRenders(prev => prev.map(r => 
        r.id === renderToRename.id ? { ...r, customName: newRenderName.trim() } : r
      ));
      
      if (selectedRender && selectedRender.id === renderToRename.id) {
        setSelectedRender({ ...selectedRender, customName: newRenderName.trim() });
      }
      
      setShowRenameRenderPopup(false);
      setRenderToRename(null);
      setNewRenderName('');
    } catch (error) {
      console.error('Error renaming render:', error);
      alert('Failed to rename render. Please try again.');
    }
  }, [renderToRename, authUser, newRenderName, selectedRender]);
  
  // ============================================
  // SAVE CUSTOM THUMBNAIL
  // ============================================
  const saveCustomThumbnail = async (render, thumbnailDataUrl) => {
    if (!authUser || !render) return;
    
    setIsGeneratingThumbnail(true);
    try {
      const renderRef = doc(db, 'renders', render.id);
      await updateDoc(renderRef, {
        thumbnailUrl: thumbnailDataUrl,
        updatedAt: serverTimestamp(),
      });
      
      setUserRenders(prev => prev.map(r => 
        r.id === render.id ? { ...r, thumbnailUrl: thumbnailDataUrl } : r
      ));
      
      if (selectedRender && selectedRender.id === render.id) {
        setSelectedRender({ ...selectedRender, thumbnailUrl: thumbnailDataUrl });
      }
      
      setShowThumbnailPicker(false);
      setThumbnailPickerRender(null);
      setThumbnailPreviewUrl(null);
      setThumbnailSeekTime(1);
    } catch (error) {
      console.error('Error saving thumbnail:', error);
      alert('Failed to save thumbnail');
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };
  
  // ============================================
  // DOWNLOAD VIDEO
  // ============================================
  const downloadVideo = async (videoUrl, renderId) => {
    if (isDownloading) {
      console.log('Download already in progress');
      return;
    }
    
    setIsDownloading(true);
    
    try {
      const render = userRenders.find(r => r.id === renderId);
      
      if (!render) {
        console.error('Render not found:', renderId);
        alert('Render not found. Please try again.');
        setIsDownloading(false);
        return;
      }
      
      let freshUrl = await getFreshVideoUrl(render);
      
      if (!freshUrl) {
        alert('Could not get video URL. Please try again.');
        setIsDownloading(false);
        return;
      }
      
      setGenerationStatus('Preparing download...');
      
      const response = await fetch(freshUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }
      
      const blob = await response.blob();
      const filename = `render_${renderId}.mp4`;
      
      saveAs(blob, filename);
      
      setGenerationStatus('');
    } catch (error) {
      console.error('Download error:', error);
      setGenerationStatus('');
      alert('Download failed. Please try again later.');
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
      }, 500);
    }
  };
  
  // ============================================
  // HANDLE REVERSE CLIP
  // ============================================
  const handleReverseClip = async () => {
    if (!selectedRender) return;
    
    setEditMenuOpen(false);
    
    if (selectedRender?.isReversed) {
      try {
        const renderRef = doc(db, 'renders', selectedRender.id);
        const originalUrl = selectedRender.originalVideoUrl || selectedRender.videoUrl;
        const originalPath = selectedRender.originalVideoStoragePath || selectedRender.videoStoragePath;
        
        await updateDoc(renderRef, {
          videoUrl: originalUrl,
          videoStoragePath: originalPath,
          isReversed: false,
          updatedAt: serverTimestamp(),
        });
        
        setUserRenders(prev => prev.map(r =>
          r.id === selectedRender.id ? { 
            ...r, 
            videoUrl: originalUrl, 
            videoStoragePath: originalPath,
            isReversed: false 
          } : r
        ));
        setSelectedRender(prev => ({ 
          ...prev, 
          videoUrl: originalUrl, 
          videoStoragePath: originalPath,
          isReversed: false 
        }));
        
        alert('Reverted to original video');
      } catch (error) {
        console.error('Failed to revert:', error);
        alert('Failed to revert to original video');
      }
      return;
    }
    
    setIsGeneratingThumbnail(true);
    setGenerationStatus('Processing reverse...');
    
    try {
      const reversed = await reverseVideo(selectedRender, authUser, setGenerationStatus, setIsGeneratingThumbnail);
      
      if (reversed && reversed.videoUrl) {
        const originalVideoUrl = selectedRender.videoUrl;
        const originalStoragePath = selectedRender.videoStoragePath;
        
        const renderRef = doc(db, 'renders', selectedRender.id);
        await updateDoc(renderRef, {
          videoUrl: reversed.videoUrl,
          videoStoragePath: reversed.videoStoragePath,
          originalVideoUrl: originalVideoUrl,
          originalVideoStoragePath: originalStoragePath,
          isReversed: true,
          updatedAt: serverTimestamp(),
        });
        
        setUserRenders(prev => prev.map(r =>
          r.id === selectedRender.id ? { 
            ...r, 
            videoUrl: reversed.videoUrl, 
            videoStoragePath: reversed.videoStoragePath,
            originalVideoUrl: originalVideoUrl,
            originalVideoStoragePath: originalStoragePath,
            isReversed: true 
          } : r
        ));
        setSelectedRender(prev => ({ 
          ...prev, 
          videoUrl: reversed.videoUrl, 
          videoStoragePath: reversed.videoStoragePath,
          originalVideoUrl: originalVideoUrl,
          originalVideoStoragePath: originalStoragePath,
          isReversed: true 
        }));
        
        setGenerationStatus('');
        alert('Video reversed successfully!');
      } else {
        throw new Error('Reverse failed - no video URL returned');
      }
    } catch (error) {
      console.error('Failed to reverse video:', error);
      setGenerationStatus('Failed to reverse video');
      alert('Failed to reverse video. Please try again.');
    } finally {
      setIsGeneratingThumbnail(false);
      setTimeout(() => setGenerationStatus(''), 2000);
    }
  };
  
  // ============================================
  // VIDEO GENERATION (Kling AI via fal.ai / Replicate)
  // ============================================
  const handleGenerateVideo = async () => {
    if (!renderUploadedImage && !renderUploadPreview) {
      alert('Please upload an image first');
      return;
    }
    
    if (!renderPrompt.trim()) {
      alert('Please enter a prompt');
      return;
    }
    
    const hasMotionReference = !!motionVideoUrl;
    const cost = calculateVideoGenerationCost({
      duration: renderDuration,
      resolution: renderResolution,
      hasMotionReference: hasMotionReference,
      modelType: 'standard'
    });
    
    setIsGeneratingVideo(true);
    setGenerationStatus('Uploading image...');
    
    const negativePrompt = "people, humans, person, man, woman, child, face, hands, body, crowd, figures, characters, portraits, reflections, mirrors, mirror reflections, reflective surfaces showing people, shadows of people, silhouettes of people, human figure, human shape, any person visible, empty of people, no humans present, warping, morphing, distortion, deformed objects";
    
    const result = await safeGenerate(
      authUser.uid,
      cost,
      async (onProgress) => {
        let imageUrl;
        if (renderUploadPreview) {
          const storage = getStorage();
          const imageRef = ref(storage, `users/${authUser.uid}/render-inputs/${Date.now()}.jpg`);
          await uploadString(imageRef, renderUploadPreview, 'data_url');
          imageUrl = await getDownloadURL(imageRef);
        }
        
        let videoUrl = null;
        let usedFalAi = false;
        
        // Try Kling Omni with motion reference
        if (hasMotionReference && motionVideoUrl) {
          try {
            const replicateBaseUrl = getReplicateBaseUrl();
            
            const replicateResponse = await axios.post(
              `${replicateBaseUrl}/v1/models/kwaivgi/kling-v3-omni-video/predictions`,
              {
                input: {
                  prompt: renderPrompt,
                  reference_images: [imageUrl],
                  reference_video: motionVideoUrl,
                  video_reference_type: "base",
                  keep_original_sound: false,
                  duration: renderDuration,
                  aspect_ratio: "9:16",
                  mode: renderResolution === "4k" ? "pro" : "standard",
                  negative_prompt: negativePrompt + ", warping, morphing, distortion, deformed objects, melting, unstable geometry, flickering, jitter, artifacts, glitch, blur, low quality",
                  generate_audio: false,
                  cfg_scale: 0.3,
                  steps: 50,
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_REPLICATE_API_TOKEN}`,
                },
              }
            );
            
            const predictionId = replicateResponse.data.id;
            const getUrl = `${replicateBaseUrl}/v1/predictions/${predictionId}`;
            
            let attempts = 0;
            const maxAttempts = 60;
            
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 5000));
              
              const pollResponse = await axios.get(getUrl, {
                headers: { 'Authorization': `Bearer ${import.meta.env.VITE_REPLICATE_API_TOKEN}` },
              });
              
              if (pollResponse.data.status === 'succeeded') {
                const output = pollResponse.data.output;
                if (typeof output === 'string') {
                  videoUrl = output;
                } else if (output && typeof output === 'object' && output.url) {
                  videoUrl = output.url;
                }
                break;
              } else if (pollResponse.data.status === 'failed' || pollResponse.data.status === 'canceled') {
                throw new Error(`Kling Omni prediction ${pollResponse.data.status}`);
              }
              attempts++;
            }
          } catch (klingError) {
            console.warn('Kling Omni with motion reference failed:', klingError.message);
          }
        }
        
        // Fallback to standard Kling
        if (!videoUrl) {
          try {
            const replicateBaseUrl = getReplicateBaseUrl();
            
            const replicateResponse = await axios.post(
              `${replicateBaseUrl}/v1/models/kwaivgi/kling-v3-omni-video/predictions`,
              {
                input: {
                  prompt: renderPrompt,
                  reference_images: [imageUrl],
                  duration: renderDuration,
                  aspect_ratio: "9:16",
                  mode: renderResolution === "4k" ? "pro" : "standard",
                  negative_prompt: negativePrompt + ", warping, morphing, distortion, deformed objects, melting, unstable geometry, flickering, jitter, artifacts, glitch, blur, low quality",
                  generate_audio: false,
                  keep_original_sound: false,
                  cfg_scale: 0.3,
                  steps: 50,
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_REPLICATE_API_TOKEN}`,
                },
              }
            );
            
            const predictionId = replicateResponse.data.id;
            const getUrl = `${replicateBaseUrl}/v1/predictions/${predictionId}`;
            
            let attempts = 0;
            const maxAttempts = 60;
            
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 5000));
              
              const pollResponse = await axios.get(getUrl, {
                headers: { 'Authorization': `Bearer ${import.meta.env.VITE_REPLICATE_API_TOKEN}` },
              });
              
              if (pollResponse.data.status === 'succeeded') {
                const output = pollResponse.data.output;
                if (typeof output === 'string') {
                  videoUrl = output;
                } else if (output && typeof output === 'object' && output.url) {
                  videoUrl = output.url;
                }
                break;
              } else if (pollResponse.data.status === 'failed' || pollResponse.data.status === 'canceled') {
                throw new Error(`Kling Omni prediction ${pollResponse.data.status}`);
              }
              attempts++;
            }
            
            if (!videoUrl) throw new Error('Kling Omni generation timed out');
          } catch (replicateError) {
            console.error('Replicate generation failed:', replicateError);
            
            // Fallback to fal.ai
            const falBaseUrl = getFalBaseUrl();
            
            const falPayload = {
              prompt: renderPrompt,
              image_url: imageUrl,
              duration: String(renderDuration),
              cfg_scale: 0.3,
              negative_prompt: negativePrompt + ", warping, morphing, distortion, deformed objects, melting, unstable geometry, flickering, jitter, artifacts, glitch, blur, low quality, hallucination, extra limbs, floating objects",
            };
            
            if (hasMotionReference && motionVideoUrl) {
              falPayload.video_url = motionVideoUrl;
              falPayload.character_orientation = "video";
              falPayload.keep_original_sound = false;
            }
            
            const falResponse = await axios.post(
              `${falBaseUrl}/fal-ai/kling-video/v3/pro/motion-control`,
              falPayload,
              {
                headers: {
                  'Authorization': `Key ${import.meta.env.VITE_FAL_AI_KEY}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            
            videoUrl = falResponse.data?.video?.url;
            if (videoUrl) {
              usedFalAi = true;
            } else {
              throw new Error('fal.ai returned no video URL');
            }
          }
        }
        
        if (!videoUrl) throw new Error('Failed to generate video with all available services');
        
        let permanentVideoUrl = videoUrl;
        let videoStoragePath = null;
        
        try {
          const videoResponse = await fetch(videoUrl);
          const videoBlob = await videoResponse.blob();
          
          const base64Video = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(videoBlob);
          });
          
          const storage = getStorage();
          const timestamp = Date.now();
          videoStoragePath = `users/${authUser.uid}/renders/${timestamp}_render.mp4`;
          const storageRef = ref(storage, videoStoragePath);
          
          await uploadString(storageRef, base64Video, 'data_url');
          permanentVideoUrl = await getDownloadURL(storageRef);
        } catch (storageError) {
          console.error('Failed to save video to Firebase Storage:', storageError);
        }
        
        const renderData = {
          userId: authUser.uid,
          prompt: renderPrompt,
          negativePrompt: negativePrompt,
          inputImageUrl: imageUrl,
          videoUrl: permanentVideoUrl,
          videoStoragePath: videoStoragePath,
          originalProviderUrl: videoUrl,
          thumbnailUrl: null,
          duration: renderDuration,
          resolution: renderResolution,
          aspectRatio: '9:16',
          cost: cost,
          provider: usedFalAi ? 'fal.ai' : 'replicate',
          hasMotionReference: hasMotionReference,
          motionReferenceUrl: hasMotionReference ? motionVideoUrl : null,
          isReversed: false,
          createdAt: serverTimestamp(),
          status: 'completed',
        };
        
        return { renderData, videoUrl: permanentVideoUrl };
      },
      {
        type: 'video',
        onStart: () => {},
        onProgress: () => {},
        onComplete: async (result) => {
          const { renderData, videoUrl } = result;
          const rendersRef = collection(db, 'renders');
          const docRef = await addDoc(rendersRef, renderData);
          generateVideoThumbnail(videoUrl, docRef.id, authUser, setUserRenders).catch(console.error);
          setRenderUploadedImage(null);
          setRenderUploadPreview(null);
          setRenderPrompt('');
          setMotionVideoUrl(null);
          setMotionVideoPreview(null);
          setMotionVideoStoragePath(null);
          setMotionVideoBlob(null);
          setMotionVideoDuration(0);
          setGenerationStatus('');
          await fetchUserRenders();
        },
        onError: (error) => {
          let errorMsg = 'Generation failed. Please try again.';
          if (error.message?.includes('All services failed')) {
            errorMsg = 'Video generation failed with all services. Please try again later.';
          } else if (error.message?.includes('motion')) {
            errorMsg = 'Motion control generation failed. Try re-recording your motion reference.';
          }
          setGenerationStatus(errorMsg);
        }
      }
    );
    
    if (result.success && updateRemainingCounts) {
      updateRemainingCounts();
    }
    
    setIsGeneratingVideo(false);
  };
  
  // ============================================
  // SCROLL HANDLER FOR HEADER TEXT
  // ============================================
  const handleScroll = (e) => {
    const myRendersSection = document.getElementById('my-renders-section');
    if (myRendersSection) {
      const rect = myRendersSection.getBoundingClientRect();
      const containerRect = e.currentTarget.getBoundingClientRect();
      if (rect.top <= containerRect.top + 100) {
        setHeaderText('My Renders');
      } else {
        setHeaderText('Renders');
      }
    }
  };
  
  // Check if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
  
  // ============================================
  // RENDER COMPONENT
  // ============================================
  return (
    <>
      {/* INLINE STYLES FOR ANIMATIONS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: translateY(0); }
          50% { opacity: 0.6; transform: translateY(-5px); }
        }
        @keyframes slideLeft {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-8px); }
        }
        @keyframes slideRight {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(8px); }
        }
        @keyframes slideUp {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes blink {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.2; }
        }
        @keyframes blinkPlaceholder {
          0%, 100% { color: #dededeff; opacity: 1; }
          50% { color: #a5a5a5ff; opacity: 1; }
        }
        .blink-placeholder::placeholder {
          animation: blinkPlaceholder 2s ease-in-out infinite;
          font-size: 0.75rem;
        }
        .small-placeholder::placeholder {
          font-size: 0.7rem;
        }

        /* Add to existing style tag - prevents black flash during transitions */
        .renders-overlay {
          background-color: rgba(0, 0, 0, 0.5) !important;
          transition: background-color 0.2s ease;
        }

        .renders-panel-container {
          transition: opacity 0.15s ease;
        }

        .guided-camera-overlay {
          transition: opacity 0.15s ease;
          opacity: 1;
        }

        /* Ensure smooth unmounting */
        .guided-camera-overlay-exit {
          opacity: 0;
        }

        /* Prevent white/black flash on mobile */
        @media (max-width: 767px) {
          .renders-panel-container {
            background: #000 !important;
          }
          .desktop-renders-view {
            background: #ffffffff !important;
          }
        }
        
        /* Mobile Styles for Renders Panel */
        @media (max-width: 767px) {
          .renders-overlay {
            padding: 0 !important;
            background-color: #ff7979ff !important;
            backdrop-filter: none !important;
            align-items: flex-start !important;
          }
          .renders-panel-container {
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
            background: #000 !important;
          }
          .desktop-renders-view {
            background: #ffffffff !important;
          }
          .renders-header {
            background: #000 !important;
            border-bottom-color: #333 !important;
          }
          .renders-header h2 {
            color: white !important;
          }
        }
        
        /* Guided Camera Overlay Styles */
        .guided-camera-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #000;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .guided-camera-preview {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
        }
        .guided-camera-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .guided-inputs-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          top: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 80px 20px 40px 20px;
          z-index: 10;
          pointer-events: none;
        }
        .guided-cards-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          pointer-events: none;
          flex: 1;
          margin-top:-10px;
          transform: translateY(0);
          min-height: 0;
          max-height: 100%;
        }
        .guided-input-card {
          background: rgba(107, 105, 105, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          width: 100%;
          max-width: 320px;
          pointer-events: auto;
          transition: all 0.3s ease;
          transform: scale(1);
          transform-origin: center center;
        }
        
        .guided-button-container {
          width: 100%;
          max-width: 320px;
          pointer-events: auto;
          flex-shrink: 0;
          display: flex;
          flex-direction: row;
          gap: 12px;
          margin-top: 16px;
        }
        .guided-action-btn {
          flex: 1;
          padding: 14px;
          border-radius: 40px;
          font-family: 'Avenir', sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          pointer-events: auto;
          text-align: center;
          white-space: nowrap;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
        }
        .guided-action-btn.continue-btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .guided-action-btn:active {
          transform: scale(0.98);
          background: rgba(255, 255, 255, 0.3);
        }
        .guided-input-label {
          font-family: 'Avenir', sans-serif;
          font-size: 0.75rem;
          color: white;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .guided-input-label svg {
          color: #6ba6ffff;
        }
        .guided-image-upload-area {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-bottom: 12px;
        }
        .guided-image-box {
          width: 120px;
          height: 213px;
          background: rgba(29, 102, 179, 0.24);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
          transition: all 0.2s ease;
          border: 2px solid transparent;
        }
        .guided-image-box.has-image {
          border: 2px solid #88b7ff;
          background: rgba(136, 183, 255, 0.2);
        }
        @keyframes marching {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -36;
          }
        }
        .marching-ants-border {
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          pointer-events: none;
          border-radius: 18px;
          z-index: 2;
          overflow: visible;
        }
        .marching-ants-border svg {
          width: 100%;
          height: 100%;
          display: block;
          overflow: visible;
        }
        .marching-ants-border rect {
          fill: none;
          stroke: #88b7ff;
          stroke-width: 4px;
          stroke-dasharray: 14 6;
          stroke-dashoffset: 0;
          animation: marching 0.5s linear infinite;
        }

         /* Pulsing animation for icons only - not the marching ants border */
        @keyframes pulse-icon {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(0.9);
          }
        }
        /* Target the plus icon and video icon inside the boxes when marching ants active */
        .guided-image-box.marching-ants > svg,
        .guided-motion-trigger-box.marching-ants > svg:first-of-type {
          animation: pulse-icon 1.2s ease-in-out infinite;
        }
        /* Ensure the marching ants border SVG does NOT pulse */
        .marching-ants-border svg {
          animation: none;
        }

        /* Motion reference box styles */
        .guided-motion-trigger-box {
          width: 120px;
          height: 213px;
          background: rgba(29, 102, 179, 0.24);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          position: relative;
          transition: all 0.2s ease;
          margin: 0 auto;
          border: 2px solid transparent;
        }
        .guided-motion-trigger-box.has-motion {
          border: 2px solid #88b7ff;
          background: rgba(136, 183, 255, 0.2);
        }
        /* Grey dashed border ONLY when image source is missing */
        .guided-motion-trigger-box.disabled {
          border: 2px dashed rgba(172, 172, 172, 0.7);
          opacity: 0.5;
          cursor: not-allowed;
        }
        /* No border when marching ants active (image source exists, no motion yet) */
        .guided-motion-trigger-box.marching-ants {
          border: 2px solid transparent;
          position: relative;
        }
        /* Motion reference container */
        .guided-motion-container {
          position: relative;
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          min-height: 213px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .guided-motion-container.expanded {
          min-height: 280px;
        }


        /* When motion is expanded, scale down slightly to maintain vertical centering */
        .guided-input-card.motion-expanded {
          margin-top: -15px;
          transform: scale(0.87);
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .guided-input-card.motion-closing {
          margin-top: 0;
          transform: scale(1);
          transition: all 0.25s ease-out;
        }
        .guided-motion-buttons-wrapper {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          margin-top: 0;
          width: 100%;
        }
        .guided-motion-buttons-wrapper.expanded {
          max-height: 80px;
          opacity: 1;
          margin-top: 16px;
        }
        .guided-motion-area {
          display: flex;
          gap: 12px;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .guided-motion-area {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .guided-motion-button {
          flex: 1;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .guided-motion-button:active {
          transform: scale(0.98);
          background: rgba(0, 0, 0, 0.6);
        }
        .guided-motion-button:active {
          transform: scale(0.98);
          background: rgba(255,255,255,0.15);
        }
        .guided-motion-button span {
          font-size: 0.7rem;
          color: white;
        }
        .guided-motion-preview {
          flex: 1;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .guided-continue-btn {
          width: 100%;
          max-width: 320px;
          padding: 16px;
          background: #88b7ff;
          border: none;
          border-radius: 40px;
          color: white;
          font-family: 'Avenir', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 16px;
          pointer-events: auto;
          box-shadow: 0 4px 20px rgba(136, 183, 255, 0.3);
        }
        .guided-continue-btn.disabled {
          background: rgba(136, 183, 255, 0.3);
          backdrop-filter: blur(10px);
          cursor: not-allowed;
          opacity: 0.7;
          box-shadow: none;
        }
        .guided-continue-btn.disabled {
          background: rgba(255, 255, 255, 0.15);
          cursor: not-allowed;
          opacity: 0.6;
        }
        .guided-continue-btn:active:not(.disabled) {
          transform: scale(0.98);
        }
        .guided-back-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: 30px;
          padding: 10px 16px;
          color: white;
          font-family: 'Avenir', sans-serif;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          z-index: 20;
          backdrop-filter: blur(5px);
        }
        .guided-title {
          position: absolute;
          top: 20px;
          left: 0;
          right: 0;
          text-align: center;
          color: white;
          font-family: 'Avenir', sans-serif;
          font-size: 1rem;
          font-weight: 500;
          z-index: 20;
          pointer-events: none;
        }
        
        /* Mobile Styles for Renders Panel */
        @media (max-width: 767px) {
          /* Add top padding for detail video on mobile */
          .detail-view-desktop {
            padding-top: 20px !important;
          }
          
          /* Ensure the back button in sidebar is visible */
          .detail-sidebar button:first-child {
            margin-right: auto;
          }
          .renders-overlay {
            padding: 0 !important;
            background-color: white !important;
            backdrop-filter: none !important;
            align-items: flex-start !important;
          }
          .renders-panel-container {
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
            background: white !important;
          }
          .renders-header {
            padding: 12px 16px !important;
            position: sticky !important;
            top: 0 !important;
            background: white !important;
            z-index: 20 !important;
            margin-bottom: 0 !important;
            border-bottom: 1px solid #eaeaea !important;
          }
          .renders-header h2 {
            font-size: 1.2rem !important;
          }
          .renders-content-scroll {
            padding: 0 0 24px 0 !important;
          }
          .desktop-renders-view {
            background: white !important;
            height: 100% !important;
            overflow-y: auto !important;
          }
          .desktop-two-column {
            flex-direction: column !important;
            padding: 16px !important;
            gap: 16px !important;
          }
          .desktop-left-column {
            flex: none !important;
            width: 100% !important;
            padding: 16px !important;
          }
          .desktop-right-column {
            flex: none !important;
            width: 100% !important;
          }
          /* Makes the render cards smaller but keeps 2 column layout */
          .desktop-right-column > div:last-child {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .desktop-right-column > div:last-child > div {
            min-width: 0 !important;
          }
          .detail-view-desktop {
            flex-direction: column !important;
            height: auto !important;
            min-height: 100% !important;
            overflow-y: visible !important;
            align-items: center !important;
            gap: 16px !important;
          }
          .detail-video-container {
            flex: none !important;
            min-height: auto !important;
            max-height: 45vh !important;
            width: 100% !important;
            max-width: 300px !important;
            margin: 0 auto !important;
            padding: 0 !important;
            background: transparent !important;
            border-radius: 12px !important;
          }
          .detail-sidebar {
            width: 100% !important;
            max-width: 100% !important;
            min-width: unset !important;
            border-left: none !important;
            border-top: 1px solid #e0e0e0 !important;
            padding-bottom: 30px !important;
            margin-top: 0 !important;
            overflow-y: visible !important;
          }
          /* Ensure scroll container has enough bottom padding */
          .renders-panel-container {
            overflow-y: auto !important;
          }
        }
        
        /* Desktop styles */
        @media (min-width: 768px) {
          .renders-header h2 {
            font-size: 1.6rem !important;
          }
        }
      `}</style>
      
      {/* GUIDED CAMERA OVERLAY — Slides in from right (MOBILE ONLY) */}
            {isMobile && showGuidedCamera && !showRenderDetails && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 1002,
    transform: 'translateX(0)',
    backgroundColor: '#000',
  }}>
    <GuidedCameraOverlay
  key={guidedCameraKey}
  authUser={authUser}
  onClose={() => {
    // If we are in the middle of switching to "My Renders", ignore the close call
    if (isSwitchingToMyRendersRef.current) {
      return;
    }
    // Close the panel immediately – no state change that could trigger a re‑render
    if (onClose) onClose();
  }}
  onComplete={(data) => {
    if (data.imagePreview) {
      setRenderUploadPreview(data.imagePreview);
      setGuidedImagePreview(data.imagePreview);
    }
    if (data.imageFile) {
      setRenderUploadedImage(data.imageFile);
      setGuidedImageUploaded(data.imageFile);
    }
    if (data.motionUrl) {
      setMotionVideoUrl(data.motionUrl);
      setMotionVideoStoragePath(data.motionStoragePath);
      setMotionVideoPreview(data.motionPreview);
      setMotionVideoBlob(data.motionBlob);
      setMotionVideoDuration(data.motionDuration);
      if (data.motionThumbnail) {
        setMotionThumbnailUrl(data.motionThumbnail);
      }
    }
    if (!guidedModeOpenedManually) {
      if (onClose) onClose();
    } else {
      setShowGuidedCamera(false);
      setGuidedCameraKey(prev => prev + 1);
    }
  }}
  onSwitchToMyRenders={(data) => {
    // Sync the guided overlay's state into the main panel
    if (data) {
      if (data.imagePreview) {
        setRenderUploadPreview(data.imagePreview);
        setGuidedImagePreview(data.imagePreview);
      }
      if (data.imageFile) {
        setRenderUploadedImage(data.imageFile);
        setGuidedImageUploaded(data.imageFile);
      }
      if (data.motionUrl) {
        setMotionVideoUrl(data.motionUrl);
        setMotionVideoStoragePath(data.motionStoragePath);
        setMotionVideoPreview(data.motionPreview);
        setMotionVideoBlob(data.motionBlob);
        setMotionVideoDuration(data.motionDuration);
        if (data.motionThumbnail) {
          setMotionThumbnailUrl(data.motionThumbnail);
        }
      }
    }
    // Prevent onClose from closing the panel while we switch
    isSwitchingToMyRendersRef.current = true;
    // Close guided mode and show the main Renders view (text interface)
    setShowGuidedCamera(false);
    setGuidedData(null);
    setGuidedCameraKey(prev => prev + 1);
    setShowRenderDetails(false);
    setGuidedModeOpenedManually(false);
    // Re‑allow onClose after the transition finishes
    setTimeout(() => {
      isSwitchingToMyRendersRef.current = false;
    }, 300);
  }}
  initialData={guidedData}
  defaultImage={initialImage || renderUploadPreview} 
/>
  </div>
)}
             
      {/* OVERLAY CONTAINER - Desktop/Tablet windowed mode (and text interface after guided flow) */}
      {(!showGuidedCamera || !isMobile) && (
        <div 
          className="renders-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: isMobile ? '#FAFAFA' : 'rgba(0, 0, 0, 0.5)',
            backdropFilter: isMobile ? 'none' : 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '0' : '20px',
          }} 
          onClick={(e) => {
          if (e.target === e.currentTarget) {
            // Fully release camera when closing panel
            if (cameraStream) {
              cameraStream.getTracks().forEach(track => track.stop());
              setCameraStream(null);
            }
            if (motionVideoRef.current) {
              motionVideoRef.current.srcObject = null;
            }
            setIsCameraReady(false);
            // Reset all states before closing
            setShowRenderDetails(false);
            setSelectedRender(null);
            setEditMenuOpen(false);
            setShowRenameRenderPopup(false);
            setShowDeleteRenderPopup(false);
            setShowThumbnailPicker(false);
            onClose();
          }
        }}
        >
          {/* PANEL CONTAINER */}
          <div 
            className="renders-panel-container"
            style={{
              backgroundColor: isMobile ? '#000' : 'white',
              borderRadius: isMobile ? '0' : '24px',
              width: '100%',
              maxWidth: isMobile ? '100%' : '1400px',
              height: isMobile ? '100%' : '90vh',
              maxHeight: isMobile ? '100%' : 'calc(100vh - 40px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: isMobile ? 'none' : '0 20px 40px rgba(0, 0, 0, 0.2)',
            }}
          >
            
            {/* DESKTOP RENDERS VIEW */}
            <div className="desktop-renders-view" style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              background: isMobile ? '#000' : 'white',
              overscrollBehavior: 'none',
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
            }}>
              
              {/* Renders Header - STICKY */}
              <div className="renders-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                flexShrink: 0, 
                position: 'sticky',
                top: 0,
                background: isMobile ? 'white' : 'white',
                zIndex: 20,
                padding: '16px 24px 12px 24px',
                borderBottom: '1px solid #eaeaea',
              }}>
                {/* LEFT SIDE: Back button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                  onClick={() => {
                    // Exit detail view if active
                    if (showRenderDetails) {
                      setShowRenderDetails(false);
                      setSelectedRender(null);
                    }
                    // Close the entire panel and return to Editor
                    // Clean up camera resources
                    if (cameraStream) {
                      cameraStream.getTracks().forEach(track => track.stop());
                      setCameraStream(null);
                    }
                    if (motionVideoRef.current) {
                      motionVideoRef.current.srcObject = null;
                    }
                    setIsCameraReady(false);
                    setEditMenuOpen(false);
                    setShowRenameRenderPopup(false);
                    setShowDeleteRenderPopup(false);
                    setShowThumbnailPicker(false);
                    if (onClose) onClose();
                  }}
                  style={{
                    background: 'rgba(217, 217, 217, 1)',
                    color: '#ffffffff',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: 'none',
                  }}
                >
                  <FiArrowLeft size={14} />
                  Back
                </button>
                  <h2 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.6rem',
                    color: isMobile ? '#333' : '#333',
                    letterSpacing: '-0.3px',
                    margin: 0,
                  }}>
                    {showRenderDetails ? 'Render Details' : 'Renders'}
                  </h2>
                </div>
                
                {/* RIGHT SIDE: Guided Mode Toggle (Mobile Only) */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {isMobile && !showRenderDetails && (
                <button
                  onClick={() => {
                  if (showGuidedCamera) {
                    // Toggling off guided mode: reset the manual flag
                    setGuidedModeOpenedManually(false);
                    const guidedEl = document.querySelector('.guided-camera-overlay')?.parentElement;
                    if (guidedEl && window.innerWidth <= 767) {
                      gsap.killTweensOf(guidedEl);
                      gsap.to(guidedEl, { x: '100%', duration: 0.5, ease: 'power2.inOut', onComplete: () => {
                        setShowGuidedCamera(false);
                        setGuidedCameraKey(prev => prev + 1);
                      }});
                    } else {
                      setShowGuidedCamera(false);
                      setGuidedCameraKey(prev => prev + 1);
                    }
                  } else {
                    // Opening guided mode manually: set the flag
                    setGuidedModeOpenedManually(true);
                    const guidedEl = document.querySelector('.guided-camera-overlay')?.parentElement;
                    if (guidedEl) {
                      gsap.killTweensOf(guidedEl);
                    }
                    // Priority: user uploaded > canvas image > previously set
                    const effectiveImagePreview = renderUploadPreview || initialImage || guidedImagePreview;
                    const effectiveImageFile = renderUploadedImage || guidedImageUploaded;
                    
                    console.log('Opening Guided Mode with image:', { 
                      fromRenderUpload: !!renderUploadPreview, 
                      fromInitialImage: !!initialImage, 
                      final: !!effectiveImagePreview 
                    });
                    
                    setGuidedData({
                      imagePreview: effectiveImagePreview,
                      imageFile: effectiveImageFile,
                      motionUrl: motionVideoUrl,
                      motionStoragePath: motionVideoStoragePath,
                      motionPreview: motionVideoPreview,
                      motionBlob: motionVideoBlob,
                      motionDuration: motionVideoDuration,
                      motionThumbnail: motionThumbnailUrl,
                    });
                    setGuidedCameraKey(prev => prev + 1);
                    setShowGuidedCamera(true);
                  }
                }}
                  style={{
                    background: '#88b7ff',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: 'none',
                  }}
                >
                  <FiCamera size={14} />
                  Guided Mode
                </button>
              )}
                </div>
              </div>
              
              {/* Content wrapper with padding */}
              <div className="renders-content-scroll" style={{ padding: '24px 24px 32px 24px' }}>
                {!showRenderDetails ? (
                  /* MAIN RENDERS VIEW - Two Column Layout */
                  <div className="desktop-two-column" style={{
                    display: 'flex',
                    gap: '24px',
                    flex: 1,
                    minHeight: 0,
                  }}>
                    {/* Left Column - Input Form */}
                    <div className="desktop-left-column" style={{
                      flex: '1 1 350px',
                      minWidth: '300px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      background: '#f8f8f8',
                      borderRadius: '16px',
                      padding: '20px',
                    }}>
                      <h3 style={{
                        fontFamily: 'Avenir, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#333',
                        margin: 0,
                      }}>
                        Generate New Video
                      </h3>
                      
                      {/* Upload Label with ratio info */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px'
                      }}>
                        <label style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666',
                        }}>
                          Image Source
                        </label>
                        <span style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.7rem',
                          color: '#88b7ff',
                          background: '#e8f0ff',
                          padding: '2px 8px',
                          borderRadius: '10px',
                        }}>
                          Output: 9:16
                        </span>
                      </div>
                      
                      {/* Image Upload Area - 9:16 Vertical */}
                      <div
                        onClick={() => {
                          // Only allow upload if not coming from canvas artboard
                          if (!renderUploadPreview || renderUploadPreview !== initialImage) {
                            renderFileInputRef.current?.click();
                          }
                        }}
                        style={{
                          width: '140px',
                          height: '249px',
                          background: renderUploadPreview ? `url(${renderUploadPreview}) center/cover no-repeat` : '#e0e0e0',
                          borderRadius: '12px',
                          border: renderUploadPreview ? '2px solid #88b7ff' : '2px dashed #ccc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: renderUploadPreview === initialImage ? 'default' : 'pointer',
                          overflow: 'hidden',
                          position: 'relative',
                          margin: '0 auto',
                        }}
                      >
                        {!renderUploadPreview && (
                          <div style={{ textAlign: 'center', color: '#999' }}>
                            <FiCamera size={28} />
                            <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', marginTop: '6px' }}>
                              Upload Image
                            </p>
                            <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.65rem', color: '#bbb', marginTop: '4px' }}>
                              9:16 ratio
                            </p>
                          </div>
                        )}
                        {renderUploadPreview && (
                          <>
                            {/* Only show "Change" label for non-canvas images */}
                            {renderUploadPreview !== initialImage && (
                              <div style={{
                                position: 'absolute',
                                bottom: '8px',
                                right: '8px',
                                background: 'rgba(0,0,0,0.6)',
                                borderRadius: '10px',
                                padding: '3px 8px',
                                color: 'white',
                                fontSize: '0.65rem',
                                fontFamily: 'Avenir, sans-serif',
                              }}>
                                Change
                              </div>
                            )}
                            {/* Only show X clear button for non-canvas images */}
                            {renderUploadPreview !== initialImage && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenderUploadPreview(null);
                                  setRenderUploadedImage(null);
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: 'rgba(0, 0, 0, 0.6)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '26px',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  color: 'white',
                                  padding: 0,
                                  zIndex: 5,
                                }}
                              >
                                <FiX size={14} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      
                      {/* Prompt Input */}
                      <div>
                        <label style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666',
                          marginBottom: '6px',
                          display: 'block',
                        }}>
                          Prompt
                        </label>
                        <textarea
                          value={renderPrompt}
                          onChange={(e) => setRenderPrompt(e.target.value)}
                          placeholder="Describe the motion and camera movement. For motion reference, describe the camera path (e.g., 'slow push through room', 'gentle orbit', 'tracking shot')..."
                          rows={3}
                          ref={(el) => {
                            if (el && renderUploadPreview && motionVideoUrl && !renderPrompt.trim()) {
                              setTimeout(() => {
                                el.focus();
                                el.select();
                              }, 350);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '12px',
                            border: (renderUploadPreview && motionVideoUrl && !renderPrompt.trim()) ? '2px solid #88b7ff' : '1px solid #ddd',
                            fontFamily: 'Roboto Mono, sans-serif',
                            fontSize: '0.85rem',
                            resize: 'none',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border 0.3s ease',
                          }}
                          className={renderUploadPreview && motionVideoUrl && !renderPrompt.trim() ? 'blink-placeholder' : 'small-placeholder'}
                        />
                        {renderPrompt.trim().length > 0 && (
                          <button
                            onClick={() => setRenderPrompt('')}
                            style={{
                              marginTop: '8px',
                              background: '#88b7ff',
                              border: 'none',
                              borderRadius: '20px',
                              padding: '6px 14px',
                              fontSize: '0.75rem',
                              fontFamily: 'Avenir, sans-serif',
                              color: 'white',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <FiX size={12} />
                            Clear Prompt
                          </button>
                        )}
                      </div>
                      
                      {/* Duration Slider */}
                      <div>
                        <label style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666',
                          marginBottom: '6px',
                          display: 'block',
                        }}>
                          Duration: {renderDuration} seconds
                        </label>
                        <input
                          type="range"
                          min="7"
                          max="15"
                          value={renderDuration}
                          onChange={(e) => setRenderDuration(parseInt(e.target.value))}
                          style={{
                            width: '100%',
                            accentColor: '#88b7ff',
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#999', fontFamily: 'Avenir, sans-serif' }}>
                          <span>7s (min)</span>
                          <span>15s (max)</span>
                        </div>
                      </div>
                      
                      {/* Aspect Ratio Info */}
                      <div style={{
                        background: '#f0f0f0',
                        borderRadius: '12px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.8rem',
                        color: '#666',
                      }}>
                        <div style={{
                          width: '16px',
                          height: '28px',
                          border: '2px solid #88b7ff',
                          borderRadius: '3px',
                          flexShrink: 0,
                        }} />
                        <div>
                          <span style={{ fontWeight: 600, color: '#333' }}>9:16 Vertical</span>
                          <span style={{ marginLeft: '6px', color: '#999' }}>• Optimized for mobile & social</span>
                        </div>
                      </div>
                      
                      {/* Resolution Selector */}
                      <div>
                        <label style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666',
                          marginBottom: '6px',
                          display: 'block',
                        }}>
                          Resolution
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setRenderResolution('720p')}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '12px',
                              border: renderResolution === '720p' ? '2px solid #88b7ff' : '1px solid #ddd',
                              background: renderResolution === '720p' ? '#e8f0ff' : 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#333',
                            }}
                          >
                            720p
                          </button>
                          <button
                            onClick={() => setRenderResolution('4k')}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '12px',
                              border: renderResolution === '4k' ? '2px solid #88b7ff' : '1px solid #ddd',
                              background: renderResolution === '4k' ? '#e8f0ff' : 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#333',
                            }}
                          >
                            4K
                          </button>
                          <button
                            disabled
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '12px',
                              border: '1px solid #ddd',
                              background: '#f5f5f5',
                              cursor: 'not-allowed',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#ccc',
                              opacity: 0.6,
                            }}
                          >
                            8K
                          </button>
                        </div>
                      </div>
                      
                                           {/* MOTION REFERENCE SECTION */}
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <RiGuideLine size={16} color="#666" />
                            <span style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'Avenir, sans-serif' }}>Motion Paths</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <span style={{
                              background: motionVideoUrl ? '#e8f0ff' : '#f0f0f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '0.6rem',
                              color: motionVideoUrl ? '#88b7ff' : '#999',
                              fontFamily: 'Avenir, sans-serif',
                              whiteSpace: 'nowrap',
                              fontWeight: motionVideoUrl ? 500 : 400,
                            }}>{motionVideoUrl ? 'Active' : 'Optional'}</span>
                            <span style={{
                              background: '#f0f0f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '0.6rem',
                              color: '#999',
                              fontFamily: 'Avenir, sans-serif',
                              whiteSpace: 'nowrap',
                            }}>Camera Dynamics</span>
                          </div>
                        </div>
                        
                        {!motionVideoUrl ? (
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {/* Record Motion Button */}
                            <button
                              onClick={openMotionRecorder}
                              style={{
                                flex: '1 1 auto',
                                padding: '14px',
                                background: '#f5f5f5',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#e8e8e8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f5f5f5';
                              }}
                            >
                              <FiCamera size={20} color="#666" />
                              <span style={{ fontSize: '0.7rem', color: '#666', fontFamily: 'Avenir, sans-serif' }}>
                                Record Motion
                              </span>
                            </button>
                            
                            {/* Upload Video Button */}
                            <button
                              onClick={() => motionFileInputRef.current?.click()}
                              style={{
                                flex: '1 1 auto',
                                padding: '14px',
                                background: '#f5f5f5',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#e8e8e8'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            >
                              <FiUpload size={20} color="#666" />
                              <span style={{ fontSize: '0.7rem', color: '#666', fontFamily: 'Avenir, sans-serif' }}>Upload Video</span>
                            </button>
                          </div>
                        ) : (
                          // Motion reference preview after capture/upload
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '8px',
                          }}>
                            <div style={{
                              width: '140px',
                              height: '175px', // 4:5 ratio, close to image source size
                              borderRadius: '12px',
                              overflow: 'hidden',
                              border: '2px solid #88b7ff',
                              position: 'relative',
                              background: '#1a1a1a',
                              margin: '0 auto',
                            }}>
                              {motionThumbnailUrl ? (
                                <img 
                                  src={motionThumbnailUrl} 
                                  alt="Motion thumbnail" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <FiVideo size={32} color="#88b7ff" />
                                </div>
                              )}
                              {/* Duration badge */}
                              <div style={{
                                position: 'absolute',
                                bottom: '6px',
                                left: '6px',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                fontSize: '0.65rem',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontFamily: 'Avenir, sans-serif',
                              }}>
                                {motionVideoDuration ? `${motionVideoDuration.toFixed(1)}s` : ''}
                              </div>
                              {/* Remove button */}
                              <button
                                onClick={handleRemoveMotionReference}
                                style={{
                                  position: 'absolute',
                                  top: '6px',
                                  right: '6px',
                                  background: 'rgba(0,0,0,0.6)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  color: 'white',
                                  zIndex: 10,
                                }}
                              >
                                <FiX size={12} />
                              </button>
                              {/* Duration warning if needed */}
                              {motionVideoDuration < 7 && motionVideoDuration > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '6px',
                                  left: '6px',
                                  background: 'rgba(224, 47, 29, 0.9)',
                                  color: 'white',
                                  fontSize: '0.55rem',
                                  padding: '1px 5px',
                                  borderRadius: '4px',
                                  fontFamily: 'Avenir, sans-serif',
                                }}>
                                  Min 7s
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Guidance text */}
                        {!motionVideoUrl && (
                          <div style={{
                            marginTop: '12px',
                            background: '#f8f8f8',
                            borderRadius: '8px',
                            padding: '8px 12px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
                              <span style={{ animation: 'slideLeft 1.5s ease infinite', fontSize: '14px' }}>←</span>
                              <span style={{ animation: 'slideRight 1.5s ease infinite 0.2s', fontSize: '14px' }}>→</span>
                              <span style={{ animation: 'slideUp 1.5s ease infinite 0.4s', fontSize: '14px' }}>↑</span>
                              <span style={{ animation: 'slideDown 1.5s ease infinite 0.6s', fontSize: '14px' }}>↓</span>
                            </div>
                            <p style={{
                              fontSize: '0.65rem',
                              fontFamily: 'Avenir, sans-serif',
                              color: '#666',
                              textAlign: 'center',
                              margin: 0,
                            }}>
                              Move your device through space to control camera motion: up to 15s
                            </p>
                            <p style={{
                              fontSize: '0.6rem',
                              fontFamily: 'Avenir, sans-serif',
                              color: '#999',
                              textAlign: 'center',
                              marginTop: '6px',
                            }}>
                               Tip: Record in minimally decorated zones to simulate camera motion & angles
                            </p>
                          </div>
                        )}
                      </div>
                      
                                            {/* Generate Button & Cost */}
                      <div style={{ marginTop: 'auto' }}>
                        {renderUploadPreview && motionVideoUrl && (
                          <div style={{
                            textAlign: 'center',
                            marginBottom: '8px',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.7rem',
                            color: '#999',
                          }}>
                            Image Source and Prompt is Required
                          </div>
                        )}
                        <button
                          onClick={handleGenerateVideo}
                          disabled={isGeneratingVideo || (!renderUploadPreview) || (!renderPrompt.trim())}
                          style={{
                            width: '100%',
                            padding: '14px',
                            borderRadius: '30px',
                            border: 'none',
                            background: (isGeneratingVideo || !renderUploadPreview || !renderPrompt.trim()) ? '#e0e0e0' : '#88b7ff',
                            color: (isGeneratingVideo || !renderUploadPreview || !renderPrompt.trim()) ? '#999' : 'white',
                            cursor: (isGeneratingVideo || !renderUploadPreview || !renderPrompt.trim()) ? 'not-allowed' : 'pointer',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '1rem',
                            fontWeight: 500,
                            transition: 'background 0.2s ease',
                          }}
                        >
                          {isGeneratingVideo ? 'Generating...' : 'Generate Video'}
                        </button>
                        <div style={{
                          textAlign: 'center',
                          marginTop: '8px',
                          padding: '6px 12px',
                          background: '#f0f0f0',
                          borderRadius: '20px',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          color: '#666',
                          display: 'block',
                          width: 'fit-content',
                          margin: '8px auto 0',
                        }}>
                          Cost: {generationCost} image equivalents {motionVideoUrl && '(includes motion reference)'}
                        </div>
                        {generationStatus && (
                          <div style={{
                            textAlign: 'center',
                            marginTop: '8px',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.8rem',
                            color: '#88b7ff',
                          }}>
                            {generationStatus}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Right Column - Renders Gallery */}
                    <div className="desktop-right-column" style={{
                      flex: '2 1 500px',
                      minWidth: '300px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <h3 style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#333',
                          margin: 0,
                        }}>
                          Your Renders {userRenders.length > 0 && `(${favoriteFilter ? userRenders.filter(r => r.isFavorite).length : userRenders.length}/${userRenders.length})`}
                        </h3>
                        <button
                          onClick={() => setFavoriteFilter(!favoriteFilter)}
                          style={{
                            background: favoriteFilter ? '#88b7ff' : 'transparent',
                            border: favoriteFilter ? 'none' : '1px solid #ddd',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <FiHeart size={14} color={favoriteFilter ? 'white' : '#888'} />
                          <span style={{
                            fontSize: '0.7rem',
                            fontFamily: 'Avenir, sans-serif',
                            color: favoriteFilter ? 'white' : '#888',
                          }}>
                            {favoriteFilter ? 'Favorites Only' : 'All Renders'}
                          </span>
                        </button>
                      </div>
                      
                      {rendersLoading ? (
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <SkeletonShimmer width="180px" height="220px" borderRadius="12px" />
                        </div>
                      ) : userRenders.length === 0 ? (
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f8f8f8',
                          borderRadius: '16px',
                          padding: '40px',
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <RiFilmAiLine size={48} color="#ccc" />
                            <p style={{
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '1rem',
                              color: '#999',
                              marginTop: '12px',
                            }}>
                              You have no renders yet
                            </p>
                            <p style={{
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.8rem',
                              color: '#bbb',
                            }}>
                              Upload an image and generate your first video
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          flex: 1,
                          overflowY: 'auto',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '12px',
                          alignContent: 'start',
                        }}>
                          {(favoriteFilter ? userRenders.filter(r => r.isFavorite) : userRenders).map((render) => (
                            <div
                              key={render.id}
                              onClick={() => {
                                setSelectedRender(render);
                                setShowRenderDetails(true);
                                setTimeout(() => {
                                  const scrollContainer = document.querySelector('.desktop-renders-view');
                                  if (scrollContainer) {
                                    scrollContainer.scrollTop = 0;
                                  }
                                }, 50);
                              }}
                              style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '1px solid #eaeaea',
                                background: 'white',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{
                                aspectRatio: '9/16',
                                background: '#e8e8e8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}>
                                {render.inputImageUrl && (
                                  <img
                                    src={render.inputImageUrl}
                                    alt="Render thumbnail"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                )}
                                {!render.inputImageUrl && (
                                  <div style={{ color: '#999', textAlign: 'center' }}>
                                    <RiFilmAiLine size={24} />
                                    <p style={{ fontSize: '0.6rem', marginTop: '4px' }}>No image</p>
                                  </div>
                                )}
                                {render.thumbnailUrl && render.thumbnailUrl !== render.inputImageUrl && (
                                  <img
                                    src={render.thumbnailUrl}
                                    alt="Video thumbnail"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      opacity: 0,
                                      transition: 'opacity 0.3s ease',
                                    }}
                                    onLoad={(e) => {
                                      e.currentTarget.style.opacity = '1';
                                      const parent = e.currentTarget.parentElement;
                                      const inputImg = parent?.querySelector('img:not([style*="position: absolute"])');
                                      if (inputImg) {
                                        inputImg.style.transition = 'opacity 0.3s ease';
                                        inputImg.style.opacity = '0';
                                      }
                                    }}
                                  />
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(render);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    background: 'rgba(0,0,0,0.5)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'white',
                                    padding: 0,
                                    zIndex: 5,
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <FiHeart size={14} fill={render.isFavorite ? 'white' : 'none'} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenderToDelete(render);
                                    setShowDeleteRenderPopup(true);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: '#e02f1d',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'white',
                                    padding: 0,
                                    zIndex: 5,
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#bb291fff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#e02f1d';
                                  }}
                                >
                                  <FaRegTrashCan size={14} />
                                </button>
                                <div style={{
                                  position: 'absolute',
                                  bottom: '8px',
                                  right: '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  alignItems: 'flex-end',
                                }}>
                                  {render.isReversed && (
                                    <div style={{
                                      background: 'rgba(0,0,0,0.6)',
                                      borderRadius: '10px',
                                      padding: '2px 6px',
                                      fontSize: '0.6rem',
                                      color: 'white',
                                      fontFamily: 'Avenir, sans-serif',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                    }}>
                                      <LuRewind size={10} />
                                      Rev
                                    </div>
                                  )}
                                  {render.hasMotionReference && (
                                    <div style={{
                                      background: 'rgba(0,0,0,0.6)',
                                      borderRadius: '10px',
                                      padding: '2px 6px',
                                      fontSize: '0.6rem',
                                      color: 'white',
                                      fontFamily: 'Avenir, sans-serif',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                    }}>
                                      <RiCameraLine size={10} />
                                      Motion
                                    </div>
                                  )}
                                  <div style={{
                                    background: 'rgba(0,0,0,0.5)',
                                    borderRadius: '10px',
                                    padding: '2px 8px',
                                    fontSize: '0.65rem',
                                    color: 'white',
                                    fontFamily: 'Avenir, sans-serif',
                                  }}>
                                    {render.duration}s
                                  </div>
                                </div>
                              </div>
                              <div style={{ padding: '10px' }}>
                                <div style={{
                                  fontFamily: 'Avenir, sans-serif',
                                  fontWeight: 500,
                                  fontSize: '0.8rem',
                                  color: '#333',
                                  marginBottom: '4px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {render.customName || render.prompt || 'Untitled Render'}
                                </div>
                                <div style={{
                                  fontFamily: 'Avenir, sans-serif',
                                  fontSize: '0.7rem',
                                  color: '#999',
                                }}>
                                  {render.resolution} • {render.createdAt?.toDate ?
                                    new Date(render.createdAt.toDate()).toLocaleDateString() :
                                    'Just now'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* RENDER DETAIL VIEW - Inside same container */
                  <div className="detail-view-desktop" style={{
                      display: 'flex',
                      flex: 1,
                      minHeight: 0,
                      paddingTop: '0px',
                      width: '100%',
                      justifyContent: 'center',
                      alignItems: 'stretch',
                      overflow: 'hidden',
                      }}>
                      {/* Left - Video Player */}
                      <div className="detail-video-container" style={{
                          flex: '1 1 0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          padding: '16px',
                      }}>
                          <div style={{
                              width: '100%',
                              maxWidth: '400px',
                              height: '100%',
                              maxHeight: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                          }}>
                              <video
                                controls
                                autoPlay
                                muted
                                loop
                                playsInline
                                webkit-playsinline="true"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  maxHeight: '100%',
                                  borderRadius: '8px',
                                  aspectRatio: '9/16',
                                  objectFit: 'contain',
                                }}
                                onError={async (e) => {
                                  console.log('Video load error, trying fresh URL...');
                                  const freshUrl = await getFreshVideoUrl(selectedRender);
                                  if (freshUrl && freshUrl !== selectedRender.videoUrl) {
                                    e.target.src = freshUrl;
                                  }
                                }}
                              >
                                <source src={selectedRender.videoUrl} type="video/mp4" />
                              </video>
                          </div>
                      </div>
                    
                      {/* Right - Details Sidebar */}
                      <div className="detail-sidebar" style={{
                          width: 'clamp(280px, 30vw, 380px)',
                          maxWidth: '380px',
                          minWidth: '280px',
                          borderLeft: '1px solid #e0e0e0',
                          background: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          flexShrink: 0,
                          }}>
                      
                      {/* Sidebar Content */}
                      <div style={{ flex: '1 1 auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '12px', paddingBottom: '16px', overflowY: 'auto' }}>
                        {/* Prompt */}
                        <div>
                          <h4 style={{
                            fontFamily: 'Avenir, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            color: '#333',
                            marginBottom: '6px',
                          }}>
                            Prompt
                          </h4>
                          <p style={{
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.85rem',
                            color: '#666',
                            margin: 0,
                            lineHeight: 1.5,
                          }}>
                            {selectedRender.prompt || 'No prompt'}
                          </p>
                        </div>
                        
                        {/* Video Information */}
                        <div>
                          <h4 style={{
                            fontFamily: 'Avenir, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#333',
                            marginBottom: '6px',
                          }}>
                            Video Information
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Duration</span>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{selectedRender.duration} seconds</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Resolution</span>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{selectedRender.resolution}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Aspect Ratio</span>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{selectedRender.aspectRatio}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Motion Paths</span>
                              <span style={{ 
                                fontFamily: 'Avenir, sans-serif', 
                                fontSize: '0.85rem', 
                                color: '#333',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {selectedRender?.hasMotionReference ? (
                                  <>
                                    <RiCameraLine size={12} />
                                    Yes
                                  </>
                                ) : 'No'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Reversed</span>
                              <span style={{ 
                                fontFamily: 'Avenir, sans-serif', 
                                fontSize: '0.85rem', 
                                color: '#333',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {selectedRender?.isReversed ? (
                                  <>
                                    <LuRewind size={12} />
                                    Yes
                                  </>
                                ) : 'No'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Cost</span>
                              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{selectedRender.cost} image equivalents</span>
                            </div>
                            {selectedRender.createdAt && (
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Created</span>
                                <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>
                                  {selectedRender.createdAt.toDate ?
                                    new Date(selectedRender.createdAt.toDate()).toLocaleString() :
                                    'Unknown'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '14px' }}>
                          {/* Download Video Button */}
                          <button
                            onClick={() => downloadVideo(selectedRender.videoUrl, selectedRender.id)}
                            disabled={isDownloading}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              background: isDownloading ? '#aaa' : '#88b7ff',
                              color: 'white',
                              borderRadius: '12px',
                              textDecoration: 'none',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              cursor: isDownloading ? 'not-allowed' : 'pointer',
                              width: '100%',
                              border: 'none',
                              opacity: isDownloading ? 0.7 : 1,
                            }}
                          >
                            {isDownloading ? (
                              <>
                                <div style={{
                                  width: '18px',
                                  height: '18px',
                                  border: '2px solid white',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite',
                                }} />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <FiDownload size={18} />
                                Download {selectedRender?.isReversed ? 'Reversed' : 'Video'}
                              </>
                            )}
                          </button>
                          
                          {/* Generate Again Button */}
                          <button
                            onClick={() => {
                              handleGenerateAgain(selectedRender);
                              setShowRenderDetails(false);
                              setSelectedRender(null);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              border: '1px solid #88b7ff',
                              borderRadius: '12px',
                              background: 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#88b7ff',
                            }}
                          >
                            <FiPlus size={18} />
                            Generate Again (Same Settings)
                          </button>
                          
                          {/* Favorite Toggle Button */}
                          <button
                            onClick={() => toggleFavorite(selectedRender)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              background: 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#333',
                            }}
                          >
                            <FiHeart size={18} fill={selectedRender?.isFavorite ? 'white' : 'none'} />
                            {selectedRender?.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                          </button>
                          
                          {/* Copy Prompt Button */}
                          <button
                            onClick={() => copyPromptToClipboard(selectedRender.prompt || '')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '12px',
                              background: 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#333',
                            }}
                          >
                            <FiCopy size={18} />
                            Copy Prompt to Clipboard
                          </button>
                          
                          {/* Edit Render Dropdown */}
                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={() => setEditMenuOpen(!editMenuOpen)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                                padding: '10px 14px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                background: 'white',
                                cursor: 'pointer',
                                fontFamily: 'Avenir, sans-serif',
                                fontSize: '0.85rem',
                                color: '#333',
                                width: '100%',
                              }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FiEdit2 size={18} />
                                Edit Render
                              </span>
                              <span>
                                {editMenuOpen ? <IoIosArrowUp size={18} /> : <IoIosArrowDown size={18} />}
                              </span>
                            </button>
                            
                            {editMenuOpen && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: 0,
                                right: 0,
                                marginBottom: '8px',
                                background: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                overflow: 'hidden',
                              }}>
                                <button
                                  onClick={() => {
                                    setRenderToRename(selectedRender);
                                    setNewRenderName(selectedRender?.customName || selectedRender?.prompt?.substring(0, 50) || 'Untitled Render');
                                    setShowRenameRenderPopup(true);
                                    setEditMenuOpen(false);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    width: '100%',
                                    border: 'none',
                                    background: 'white',
                                    cursor: 'pointer',
                                    fontFamily: 'Avenir, sans-serif',
                                    fontSize: '0.85rem',
                                    color: '#333',
                                    textAlign: 'left',
                                    borderBottom: '1px solid #f0f0f0',
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <FiEdit2 size={14} />
                                  Rename Render
                                </button>
                                
                                <button
                                  onClick={async () => {
                                    setThumbnailPickerRender(selectedRender);
                                    setThumbnailSeekTime(1);
                                    setThumbnailPreviewUrl(null);
                                    setEditMenuOpen(false);
                                    setShowThumbnailPicker(true);
                                    setTimeout(() => {
                                      const videoEl = document.getElementById('hidden-video-seek');
                                      if (videoEl && selectedRender?.videoUrl) {
                                        videoEl.src = selectedRender.videoUrl;
                                        videoEl.load();
                                      }
                                    }, 50);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    width: '100%',
                                    border: 'none',
                                    background: 'white',
                                    cursor: 'pointer',
                                    fontFamily: 'Avenir, sans-serif',
                                    fontSize: '0.85rem',
                                    color: '#333',
                                    textAlign: 'left',
                                    borderBottom: '1px solid #f0f0f0',
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <FiImage size={14} />
                                  Change Thumbnail
                                </button>
                                
                                <button
                                  onClick={handleReverseClip}
                                  disabled={isGeneratingThumbnail}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    width: '100%',
                                    border: 'none',
                                    background: 'white',
                                    cursor: isGeneratingThumbnail ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Avenir, sans-serif',
                                    fontSize: '0.85rem',
                                    color: '#333',
                                    textAlign: 'left',
                                    borderBottom: '1px solid #f0f0f0',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isGeneratingThumbnail) e.currentTarget.style.background = '#f5f5f5';
                                  }}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <LuRewind size={14} />
                                  {selectedRender?.isReversed ? 'Revert to Original' : 'Reverse Clip'}
                                </button>
                                
                                <button
                                  disabled
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    width: '100%',
                                    border: 'none',
                                    background: 'white',
                                    cursor: 'not-allowed',
                                    fontFamily: 'Avenir, sans-serif',
                                    fontSize: '0.85rem',
                                    color: '#ccc',
                                    textAlign: 'left',
                                  }}
                                >
                                  <FiPlus size={14} />
                                  Coming Soon
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => {
                              setRenderToDelete(selectedRender);
                              setShowDeleteRenderPopup(true);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 14px',
                              border: '1px solid #ffcccc',
                              borderRadius: '12px',
                              background: 'white',
                              cursor: 'pointer',
                              fontFamily: 'Avenir, sans-serif',
                              fontSize: '0.85rem',
                              color: '#e02f1d',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fff5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                            }}
                          >
                            <FaRegTrashCan size={18} />
                            Delete Render
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={renderFileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleRenderImageUpload}
      />
      <input
        type="file"
        ref={motionFileInputRef}
        accept="video/mp4,video/quicktime,video/webm"
        style={{ display: 'none' }}
        onChange={handleMotionFileUpload}
      />
      
      {/* MOTION RECORDER MODAL - Fullscreen Camera */}
      {showMotionRecorder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'black',
          zIndex: 3000,
          display: 'flex',
          flexDirection: 'column',
        }}>
 
          
          {/* Live Camera Preview */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <video
            ref={motionVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          </div>
          
          {/* Camera not ready overlay */}
          {!isCameraReady && !cameraPermissionDenied && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 15,
            }}>
              <div style={{ textAlign: 'center', color: 'white' }}>
                <div style={{ width: '40px', height: '40px', border: '3px solid white', borderTop: '3px solid #88b7ff', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
                <p style={{ fontFamily: 'Avenir, sans-serif' }}>Initializing camera...</p>
              </div>
            </div>
          )}
          
          {/* Permission denied overlay */}
          {cameraPermissionDenied && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 15,
              flexDirection: 'column',
              gap: '20px',
            }}>
              <p style={{ fontFamily: 'Avenir, sans-serif', color: 'white', textAlign: 'center', padding: '20px' }}>
                Camera access denied. Please enable camera permissions in your browser settings, or upload a video instead.
              </p>
              <button
                onClick={() => motionFileInputRef.current?.click()}
                style={{
                  padding: '12px 24px',
                  background: '#88b7ff',
                  border: 'none',
                  borderRadius: '30px',
                  color: 'white',
                  fontFamily: 'Avenir, sans-serif',
                  cursor: 'pointer',
                }}
              >
                Upload Video Instead
              </button>
              <button
                onClick={cancelMotionRecording}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  border: '1px solid white',
                  borderRadius: '30px',
                  color: 'white',
                  fontFamily: 'Avenir, sans-serif',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
          
          {/* Overlay UI - only show when camera is ready */}
          {isCameraReady && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
              padding: '24px 20px calc(40px + env(safe-area-inset-bottom, 0px))',
              color: 'white',
              zIndex: 10,
            }}>
              {/* Duration Progress Bar - Smooth */}
                            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.8rem', fontFamily: 'Avenir, sans-serif' }}>
                  <span>{recordingDuration.toFixed(1)}s</span>
                  <span></span>
                </div>
                <div style={{
                  height: '6px',
                  background: 'rgba(255,255,255,0.3)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${(recordingDuration / 15) * 100}%`,
                    height: '100%',
                    background: recordingDuration >= 7 
                      ? '#4CAF50' 
                      : recordingDuration >= 5 
                        ? '#FFA500' 
                        : recordingDuration >= 3 
                          ? '#FF8C00' 
                          : '#FF3B30',
                    transition: 'width 0.05s linear, background 0.3s ease',
                    borderRadius: '3px',
                  }} />
                </div>
              </div>
              
              {/* Guidance Arrows */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '28px', marginBottom: '8px' }}>
                  <span style={{ animation: 'slideLeft 1s ease infinite' }}>←</span>
                  <span style={{ animation: 'slideRight 1s ease infinite 0.2s' }}>→</span>
                  <span style={{ animation: 'slideUp 1s ease infinite 0.4s' }}>↑</span>
                  <span style={{ animation: 'slideDown 1s ease infinite 0.6s' }}>↓</span>
                </div>
                <p style={{ fontSize: '0.85rem', fontFamily: 'Avenir, sans-serif', marginBottom: '6px' }}>
                  Move your device through space to control motion
                </p>
                <p style={{ fontSize: '0.65rem', opacity: 0.7, fontFamily: 'Avenir, sans-serif' }}>
                  Tip: Slow and steady movement, Record in minimally designed or empty area
                </p>
                {recordingDuration > 0 && recordingDuration < 7 && (
                  <p style={{ fontSize: '0.65rem', color: '#ffaa00', marginTop: '6px' }}>
                    Need {(7 - recordingDuration).toFixed(1)} more seconds: Minimum 7s required
                  </p>
                )}
                {recordingDuration >= 7 && (
                  <p style={{ fontSize: '0.65rem', color: '#4CAF50', marginTop: '6px' }}>
                    ✓ Minimum duration reached
                  </p>
                )}
              </div>
              
              {/* Control Buttons - 4 inline horizontal */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={cancelMotionRecording}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '30px',
                    color: 'white',
                    fontSize: '0.85rem',
                    fontFamily: 'Avenir, sans-serif',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={isRecordingMotion ? stopMotionRecording : startMotionRecording}
                  style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    background: isRecordingMotion ? '#ff3b30' : '#818181ff',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.1s ease',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  }}
                  onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                  onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {isRecordingMotion ? (
                    <div style={{ width: '28px', height: '28px', background: 'white', borderRadius: '4px' }} />
                  ) : (
                    <div style={{
                      width: '58px',
                      height: '58px',
                      background: 'white',
                      borderRadius: '50%',
                    }} />
                  )}
                </button>
                
                <button
                  onClick={() => {
                    const newZoom = cameraZoomLevel === 1 ? 0.5 : 1;
                    setCameraZoomLevel(newZoom);
                    if (cameraStream) {
                      const videoTrack = cameraStream.getVideoTracks()[0];
                      const capabilities = videoTrack.getCapabilities?.() || {};
                      if (capabilities.zoom) {
                        const clampedZoom = Math.min(Math.max(newZoom, capabilities.zoom.min || 1), capabilities.zoom.max || 1);
                        videoTrack.applyConstraints({ advanced: [{ zoom: clampedZoom }] })
                          .then(() => {
                            console.log('Zoom set to:', clampedZoom);
                          })
                          .catch((err) => {
                            console.warn('Zoom constraint failed:', err);
                          });
                      } else {
                        console.warn('Zoom not supported on this device');
                      }
                    }
                  }}
                  style={{
                    padding: '10px 18px',
                    background: cameraZoomLevel === 0.5 ? '#88b7ff' : 'rgba(255,255,255,0.2)',
                    border: cameraZoomLevel === 0.5 ? 'none' : '1px solid rgba(255,255,255,0.5)',
                    borderRadius: '30px',
                    color: 'white',
                    fontSize: '0.8rem',
                    fontFamily: 'Avenir, sans-serif',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {cameraZoomLevel === 0.5 ? '0.5x' : '1x'}
                </button>
                
                <button
                  onClick={() => motionFileInputRef.current?.click()}
                  style={{
                    padding: '8px 12px',
                    background: '#555555',
                    border: 'none',
                    borderRadius: '30px',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontFamily: 'Avenir, sans-serif',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#666666'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#555555'}
                >
                  <FiImage size={14} color="white" />
                  Upload
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* MOTION PREVIEW MODAL - Review Recorded Motion */}
      {showMotionPreview && motionPreviewBlobUrl && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000',
          zIndex: 3001,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Fullscreen Video Preview */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <video
              ref={motionPreviewVideoRef}
              src={motionPreviewBlobUrl}
              autoPlay
              loop
              muted
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              x-webkit-airplay="allow"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                background: '#000',
              }}
              onError={(e) => {
                console.error('Motion preview video error:', e);
                const videoEl = e.target;
                console.log('Video error details - src:', videoEl.src?.substring(0, 50), 'readyState:', videoEl.readyState, 'networkState:', videoEl.networkState, 'error:', videoEl.error);
              }}
              onLoadedData={() => {
                console.log('Motion preview video loaded successfully');
              }}
            />
            
            {/* Glass overlay info bar - positioned over the video */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(107, 105, 105, 0.15)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '20px 20px calc(55px + env(safe-area-inset-bottom, 0px))',
            }}>
              <div style={{
                maxWidth: '400px',
                margin: '0 auto',
              }}>
                <div style={{ marginBottom: '14px', textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  fontFamily: 'Avenir, sans-serif', 
                  color: 'white', 
                  marginBottom: '4px',
                  fontWeight: 500,
                }}>
                  Motion Paths Recorded
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  fontFamily: 'Avenir, sans-serif', 
                  color: 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}>
                  <span>Duration: {motionVideoDuration.toFixed(1)}s</span>
                  {motionVideoDuration < 7 && (
                    <span style={{ 
                      color: '#FFA500', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px',
                    }}>
                      <IoWarningOutline size={13} />
                      Min 7s required
                    </span>
                  )}
                  {motionVideoDuration >= 7 && (
                    <span style={{ 
                      color: '#4CAF50',
                    }}>
                      Ready
                    </span>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleReRecordMotion}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    borderRadius: '30px',
                    color: 'white',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Re-Record
                </button>
                <button
                  onClick={handleConfirmMotion}
                  disabled={motionVideoDuration < 7}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: motionVideoDuration < 7 ? 'rgba(136, 183, 255, 0.4)' : '#88b7ff',
                    border: 'none',
                    borderRadius: '30px',
                    color: 'white',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.85rem',
                    cursor: motionVideoDuration < 7 ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    boxShadow: motionVideoDuration < 7 ? 'none' : '0 4px 15px rgba(136, 183, 255, 0.3)',
                    opacity: motionVideoDuration < 7 ? 0.6 : 1,
                  }}
                >
                  {motionVideoDuration < 7 ? `Need ${(7 - motionVideoDuration).toFixed(1)}s (min 7s required)` : 'Use This Motion'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* DELETE RENDER CONFIRMATION POPUP */}
      {showDeleteRenderPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowDeleteRenderPopup(false)}>
          <div style={{
            backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '25px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Delete Render</h3>
            <p style={{ color: '#666', marginBottom: '24px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.95rem', lineHeight: 1.5 }}>
              This render will be permanently deleted from your account. Are you sure you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteRenderPopup(false);
                  setRenderToDelete(null);
                }}
                style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ccc',
                  borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                No, Cancel
              </button>
              <button
                onClick={handleDeleteRender}
                style={{
                  padding: '10px 20px', backgroundColor: '#e02f1d', border: 'none',
                  borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* RENAME RENDER POPUP */}
      {showRenameRenderPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowRenameRenderPopup(false)}>
          <div style={{
            backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '25px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Rename Video</h3>
            <input
              type="text"
              value={newRenderName}
              onChange={(e) => setNewRenderName(e.target.value)}
              placeholder="Enter new video name"
              autoFocus
              style={{
                width: '100%', padding: '12px 16px', marginBottom: '15px',
                backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px',
                fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', boxSizing: 'border-box'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRenameRender();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowRenameRenderPopup(false);
                  setRenderToRename(null);
                  setNewRenderName('');
                }}
                style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ccc',
                  borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameRender}
                disabled={!newRenderName.trim()}
                style={{
                  padding: '10px 20px', backgroundColor: '#88B7FF', border: 'none',
                  borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif',
                  opacity: !newRenderName.trim() ? 0.5 : 1,
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* THUMBNAIL PICKER POPUP */}
      {showThumbnailPicker && thumbnailPickerRender && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => {
          setShowThumbnailPicker(false);
          setThumbnailPickerRender(null);
          setThumbnailPreviewUrl(null);
          setThumbnailSeekTime(1);
        }}>
          <div style={{
            backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '25px',
            maxWidth: '500px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '8px', fontFamily: 'avenir-lt-pro, sans-serif' }}>Change Thumbnail</h3>
            <p style={{ color: '#666', fontSize: '0.8rem', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif' }}>
              Drag the slider to select a frame
            </p>
            
            {/* Visible Video Player */}
            <div style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
            }}>
              <div style={{
                aspectRatio: '9/16',
                width: '180px',
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#000',
              }}>
                <video
                  id="thumbnail-preview-video"
                  src={thumbnailPickerRender?.videoUrl}
                  crossOrigin="anonymous"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  muted
                  playsInline
                  preload="auto"
                  onLoadedMetadata={(e) => {
                    const video = e.target;
                    const duration = Math.min(video.duration, 15);
                    const slider = document.getElementById('thumbnail-time-slider');
                    if (slider) {
                      slider.max = duration;
                      const durationDisplay = document.getElementById('video-duration-display');
                      if (durationDisplay) {
                        durationDisplay.textContent = `/ ${duration.toFixed(1)} sec`;
                      }
                    }
                    video.currentTime = 1;
                    video.addEventListener('seeked', function capture() {
                      if (video.readyState >= 2 && video.videoWidth > 0) {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(video, 0, 0);
                        const frame = canvas.toDataURL('image/jpeg', 0.85);
                        setThumbnailPreviewUrl(frame);
                        setThumbnailSeekTime(video.currentTime);
                        video.removeEventListener('seeked', capture);
                      }
                    }, { once: true });
                  }}
                />
              </div>
            </div>
            
            {/* Timeline Slider */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontSize: '0.8rem',
                  color: '#666',
                }}>
                  Selected frame at {thumbnailSeekTime.toFixed(1)}s 
                </label>
                <span id="video-duration-display" style={{
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontSize: '0.7rem',
                  color: '#999',
                }}>
                  / loading...
                </span>
              </div>
              <input
                id="thumbnail-time-slider"
                type="range"
                min="0.1"
                max="15"
                step="0.1"
                defaultValue="1"
                onInput={(e) => {
                  const newTime = parseFloat(e.target.value);
                  setThumbnailSeekTime(newTime);
                  setThumbnailPreviewUrl(null);
                  const videoEl = document.getElementById('thumbnail-preview-video');
                  if (videoEl) {
                    videoEl.currentTime = newTime;
                  }
                }}
                onMouseUp={(e) => {
                  const videoEl = document.getElementById('thumbnail-preview-video');
                  if (videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
                    setTimeout(() => {
                      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
                        const canvas = document.createElement('canvas');
                        canvas.width = videoEl.videoWidth;
                        canvas.height = videoEl.videoHeight;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(videoEl, 0, 0);
                        const frame = canvas.toDataURL('image/jpeg', 0.85);
                        setThumbnailPreviewUrl(frame);
                        setThumbnailSeekTime(videoEl.currentTime);
                      }
                    }, 300);
                  }
                }}
                onTouchEnd={(e) => {
                  const videoEl = document.getElementById('thumbnail-preview-video');
                  if (videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
                    setTimeout(() => {
                      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
                        const canvas = document.createElement('canvas');
                        canvas.width = videoEl.videoWidth;
                        canvas.height = videoEl.videoHeight;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(videoEl, 0, 0);
                        const frame = canvas.toDataURL('image/jpeg', 0.85);
                        setThumbnailPreviewUrl(frame);
                        setThumbnailSeekTime(videoEl.currentTime);
                      }
                    }, 300);
                  }
                }}
                style={{
                  width: '100%',
                  accentColor: '#88b7ff',
                }}
              />
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowThumbnailPicker(false);
                  setThumbnailPickerRender(null);
                  setThumbnailPreviewUrl(null);
                  setThumbnailSeekTime(1);
                }}
                style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ccc',
                  borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (isGeneratingThumbnail) return;
                  if (!thumbnailPreviewUrl) {
                    alert('Please select a frame first by releasing the slider.');
                    return;
                  }
                  
                  setIsGeneratingThumbnail(true);
                  
                  try {
                    await saveCustomThumbnail(thumbnailPickerRender, thumbnailPreviewUrl);
                    
                    setShowThumbnailPicker(false);
                    setThumbnailPickerRender(null);
                    setThumbnailPreviewUrl(null);
                    setThumbnailSeekTime(1);
                  } catch (error) {
                    console.error('Error saving thumbnail:', error);
                    alert('Failed to save thumbnail');
                  } finally {
                    setIsGeneratingThumbnail(false);
                  }
                }}
                disabled={isGeneratingThumbnail || !thumbnailPreviewUrl}
                style={{
                  padding: '10px 20px', backgroundColor: '#88B7FF', border: 'none',
                  borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif',
                  opacity: (isGeneratingThumbnail || !thumbnailPreviewUrl) ? 0.5 : 1,
                }}
              >
                {isGeneratingThumbnail ? 'Saving...' : 'Save as Thumbnail'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden video element for thumbnail seeking (pre-warm) */}
      <video
        id="hidden-video-seek"
        style={{ display: 'none' }}
        crossOrigin="anonymous"
        preload="auto"
      />
    </>
  );
};

export default RendersPanel;